import React from "react";
import { fetchClimateAlertsBase, fetchClimateForecastBase, fetchClimatePeriodSummaries, fetchClimateDailyHistory, fetchFarmClimateSummary } from "../lib/climateApi";
import { normalizeAdminGlobalsConfig } from "../lib/adminGlobals";
import { getFarmClimateProfile, normalizeClimateStations, normalizeClimateDatabaseSummary, computeClimateCapacityAdjustment } from "../lib/climateConfig";
import { fmtDate } from "../lib/formatters";
import { buildClimateVisualMeta, buildClimateMaxVisualMeta, buildClimateDailyVisualMeta, buildForecastVisualMeta } from "../lib/climateVisuals";
import { computePastureCapacity } from "../lib/pastureCapacity";
import { isDeletedItem } from "../lib/syncUtils";
import { appendClimateTelemetry } from "../lib/climateTelemetry";


function toPositiveNumber(value, fallback = 0) {
  const num = Number(value);
  return Number.isFinite(num) && num >= 0 ? num : fallback;
}

function buildClimateWindowSummary(summary, windowDays) {
  const normalized = normalizeClimateDatabaseSummary(summary);
  const key = Number(windowDays) === 15 ? 15 : (Number(windowDays) === 90 ? 90 : 30);
  return {
    latest_observed_on: normalized.latestObservedOn,
    effective_source: normalized.effectiveSource,
    integration_status: normalized.integrationStatus,
    windows: normalized.windows,
    windowsRaw: normalized.windows,
    source_station_name: normalized.sourceStationName,
    source_station_id: normalized.sourceStationId,
    windowDays: key,
  };
}

function getImpactMeta(level = "attention") {
  const key = String(level || "attention").trim() || "attention";
  if (key === "favorable") return { label: "Favorável", color: "#22C55E", icon: "🌱" };
  if (key === "critical") return { label: "Crítica", color: "#EF4444", icon: "🛑" };
  if (key === "restrictive") return { label: "Restritiva", color: "#F97316", icon: "⚠️" };
  return { label: "Atenção", color: "#F59E0B", icon: "👀" };
}

function resolvePastureImpactLevel(climateCalc, hasClimateData) {
  const factor = Number(climateCalc?.factor || 1);
  if (!hasClimateData) return "attention";
  if (factor >= 1.03) return "favorable";
  if (factor >= 0.93) return "attention";
  if (factor >= 0.82) return "restrictive";
  return "critical";
}

function buildPastureImpactNarrative(level, climateCalc, monitoredPastures) {
  const count = Number(monitoredPastures || 0);
  if (level === "favorable") return count
    ? `A janela climática recente favorece as pastagens monitoradas da fazenda. Há sinal de suporte melhor à capacidade dos ${count} local(is) pecuários acompanhados.`
    : "A janela climática recente está favorável para pastagens, mas a fazenda ainda não tem locais pecuários suficientes cadastrados para leitura operacional.";
  if (level === "critical") return count
    ? `O clima recente pressiona fortemente a capacidade das ${count} pastagens monitoradas. O Trembo recomenda reduzir risco de carga excessiva até a próxima melhora da janela.`
    : "O clima recente está crítico para pastagens. Vale segurar decisões de aumento de lotação até haver recuperação da janela climática.";
  if (level === "restrictive") return count
    ? `A janela climática recente reduz parte da folga operacional das ${count} pastagens monitoradas. O manejo deve ser mais conservador nos próximos dias.`
    : "A janela climática recente está restritiva para pastagens. O manejo deve ser mais conservador até nova leitura.";
  return count
    ? `O clima recente pede atenção nas ${count} pastagens monitoradas. Ainda não é um cenário crítico, mas vale acompanhar lotação, altura e chuva com mais frequência.`
    : "O clima recente pede atenção para pastagens. Ainda não é um cenário crítico, mas vale acompanhar a próxima sequência de leituras.";
}

function buildPastureImpactGuidance(level, trendDirection) {
  if (level === "favorable") return trendDirection === "improving"
    ? "Janela positiva e melhorando. Dá para planejar rotação e observação de ganho de pasto sem relaxar o acompanhamento."
    : "Janela positiva. Mantenha a medição de altura em dia e aproveite a fase favorável para organizar a ocupação dos lotes.";
  if (level === "critical") return "Evite aumentar lotação agora. Revise redistribuição, suplementação, água disponível e prioridade dos piquetes até sair da faixa crítica.";
  if (level === "restrictive") return "Conduza a lotação de forma conservadora. Revise altura da pastagem, permanência dos lotes e sinais de queda de suporte antes de avançar.";
  return "Siga monitorando a próxima leitura e confirme altura das áreas mais usadas. Pequenas decisões de manejo agora ajudam a evitar pressão depois.";
}

function buildTrendMeta(currentCalc, calc15, calc30, calc90) {
  const factorCurrent = Number(currentCalc?.factor || calc30?.factor || 1);
  const factor15 = Number(calc15?.factor || factorCurrent);
  const factor90 = Number(calc90?.factor || calc30?.factor || factorCurrent);
  const shortDelta = factor15 - factorCurrent;
  const longDelta = factorCurrent - factor90;
  let direction = "stable";
  let label = "Tendência estável";
  let detail = "A capacidade climática ajustada está andando de forma parecida entre as janelas recentes.";
  if (shortDelta >= 0.03 || longDelta >= 0.05) {
    direction = "improving";
    label = "Melhorando";
    detail = "A janela curta está mais favorável do que a base usada agora, sinalizando possível recuperação gradual das pastagens.";
  } else if (shortDelta <= -0.03 || longDelta <= -0.05) {
    direction = "worsening";
    label = "Piorando";
    detail = "A janela curta perdeu força frente à base e sugere aumento de pressão climática sobre as pastagens.";
  }
  return { direction, label, detail, currentFactor: factorCurrent, shortFactor: factor15, baseFactor: Number(calc30?.factor || factorCurrent), longFactor: factor90 };
}

function buildImpactFactorRows({ normalizedSummary, climateCalc, trendMeta, monitoredPastures, baseUaTotal, adjustedUaTotal, sourceLabel, fmtDate }) {
  const observedOn = normalizedSummary?.latestObservedOn ? fmtDate(normalizedSummary.latestObservedOn) : "Sem leitura no banco";
  return [
    { id: "source", label: "Estação / fonte", value: sourceLabel || "Sem estação principal", helper: observedOn },
    { id: "window", label: "Janela usada", value: `${climateCalc?.windowDays || 30} dias`, helper: climateCalc?.summary || "Sem resumo climático" },
    { id: "rain", label: "Chuva acumulada", value: `${Number(climateCalc?.rainfallMm || 0).toFixed(1)} mm`, helper: "Usada no ajuste climático da capacidade" },
    { id: "dry", label: "Dias secos", value: `${Math.round(Number(climateCalc?.dryDays || 0))}`, helper: "Quanto maior, maior a pressão sobre a pastagem" },
    { id: "temp", label: "Temperatura média", value: climateCalc?.avgTemp == null ? "—" : `${Number(climateCalc.avgTemp).toFixed(1)} °C`, helper: "Ajuda a compor a leitura da janela climática" },
    { id: "factor", label: "Fator climático", value: climateCalc?.factorLabel || "1.00x", helper: "Multiplicador aplicado sobre a capacidade base" },
    { id: "pastures", label: "Pastagens monitoradas", value: `${monitoredPastures}`, helper: monitoredPastures ? "Locais pecuários ativos considerados nesta leitura" : "Nenhuma pastagem pecuária ativa cadastrada" },
    { id: "capacity", label: "Capacidade ajustada", value: `${adjustedUaTotal.toFixed(2)} UA`, helper: baseUaTotal > 0 ? `Base ${baseUaTotal.toFixed(2)} UA · tendência ${trendMeta.label.toLowerCase()}` : "Sem capacidade base definida nas pastagens" },
  ];
}



function averageOf(values = [], digits = 1) {
  const nums = (Array.isArray(values) ? values : []).map((value) => Number(value)).filter((value) => Number.isFinite(value));
  if (!nums.length) return null;
  const total = nums.reduce((sum, value) => sum + value, 0);
  return Number((total / nums.length).toFixed(digits));
}

function sumOf(values = [], digits = 1) {
  const nums = (Array.isArray(values) ? values : []).map((value) => Number(value)).filter((value) => Number.isFinite(value));
  if (!nums.length) return null;
  return Number(nums.reduce((sum, value) => sum + value, 0).toFixed(digits));
}

function buildComparisonWindowStats(items = []) {
  const source = Array.isArray(items) ? items : [];
  return {
    readings: source.length,
    avgTemp: averageOf(source.map((item) => {
      const min = item?.temp_min_c == null || item?.temp_min_c === '' ? null : Number(item.temp_min_c);
      const max = item?.temp_max_c == null || item?.temp_max_c === '' ? null : Number(item.temp_max_c);
      if (min == null && max == null) return null;
      return ((min ?? max ?? 0) + (max ?? min ?? 0)) / 2;
    }), 1),
    avgHumidity: averageOf(source.map((item) => {
      const min = item?.humidity_min_pct == null || item?.humidity_min_pct === '' ? null : Number(item.humidity_min_pct);
      const max = item?.humidity_max_pct == null || item?.humidity_max_pct === '' ? null : Number(item.humidity_max_pct);
      if (min == null && max == null) return null;
      return ((min ?? max ?? 0) + (max ?? min ?? 0)) / 2;
    }), 0),
    rainfall: sumOf(source.map((item) => Number(item?.rainfall_mm || 0)), 1),
    avgPressure: averageOf(source.map((item) => item?.pressure_hpa == null || item?.pressure_hpa === '' ? null : Number(item.pressure_hpa)), 1),
    startDate: normalizeHistoryDateValue(source[source.length - 1]?.observed_on),
    endDate: normalizeHistoryDateValue(source[0]?.observed_on),
  };
}

function buildDeltaMeta(currentValue, previousValue, { digits = 1, unit = '', higherIsPositive = true } = {}) {
  if (currentValue == null || previousValue == null) {
    return { label: 'Comparação indisponível', tone: 'neutral', diff: null };
  }
  const rawDiff = Number(currentValue) - Number(previousValue);
  const diff = Number(rawDiff.toFixed(digits));
  if (Math.abs(diff) < (digits === 0 ? 1 : 0.1)) {
    return { label: 'Praticamente estável', tone: 'neutral', diff };
  }
  const improved = higherIsPositive ? diff > 0 : diff < 0;
  const tone = improved ? 'positive' : 'attention';
  const signal = diff > 0 ? '+' : '';
  return {
    label: `${signal}${diff.toFixed(digits)}${unit ? ` ${unit}` : ''} vs período anterior`,
    tone,
    diff,
  };
}

function buildComparisonCards(historyItems = [], windowDays = 30) {
  const sorted = (Array.isArray(historyItems) ? historyItems : [])
    .slice()
    .filter((item) => normalizeHistoryDateValue(item?.observed_on))
    .sort((a, b) => String(b?.observed_on || '').localeCompare(String(a?.observed_on || '')));
  const safeWindow = Math.max(1, Number(windowDays) || 30);
  const currentWindow = sorted.slice(0, safeWindow);
  const previousWindow = sorted.slice(safeWindow, safeWindow * 2);
  const currentStats = buildComparisonWindowStats(currentWindow);
  const previousStats = buildComparisonWindowStats(previousWindow);
  const enoughData = currentWindow.length > 0 && previousWindow.length > 0;
  const cards = [
    {
      id: 'avg_temp',
      title: 'Temperatura média',
      accent: '🌡️',
      value: currentStats.avgTemp == null ? '—' : `${currentStats.avgTemp.toFixed(1)} °C`,
      previousValue: previousStats.avgTemp == null ? '—' : `${previousStats.avgTemp.toFixed(1)} °C`,
      delta: buildDeltaMeta(currentStats.avgTemp, previousStats.avgTemp, { digits: 1, unit: '°C', higherIsPositive: false }),
      narrative: currentStats.avgTemp == null || previousStats.avgTemp == null
        ? 'Ainda não há base suficiente para comparar a temperatura média entre as duas janelas.'
        : currentStats.avgTemp > previousStats.avgTemp
          ? 'A janela atual está mais quente do que a anterior equivalente.'
          : (currentStats.avgTemp < previousStats.avgTemp
            ? 'A janela atual está mais amena do que a anterior equivalente.'
            : 'A temperatura média das duas janelas ficou praticamente igual.'),
    },
    {
      id: 'avg_humidity',
      title: 'Umidade média',
      accent: '💧',
      value: currentStats.avgHumidity == null ? '—' : `${currentStats.avgHumidity.toFixed(0)}%`,
      previousValue: previousStats.avgHumidity == null ? '—' : `${previousStats.avgHumidity.toFixed(0)}%`,
      delta: buildDeltaMeta(currentStats.avgHumidity, previousStats.avgHumidity, { digits: 0, unit: 'p.p.', higherIsPositive: true }),
      narrative: currentStats.avgHumidity == null || previousStats.avgHumidity == null
        ? 'Ainda não há base suficiente para comparar a umidade média entre as duas janelas.'
        : currentStats.avgHumidity > previousStats.avgHumidity
          ? 'A janela atual está com umidade média mais alta do que a anterior equivalente.'
          : (currentStats.avgHumidity < previousStats.avgHumidity
            ? 'A janela atual está mais seca do que a anterior equivalente.'
            : 'A umidade média das duas janelas ficou praticamente igual.'),
    },
    {
      id: 'rainfall',
      title: 'Chuva acumulada',
      accent: '🌧️',
      value: currentStats.rainfall == null ? '—' : `${currentStats.rainfall.toFixed(1)} mm`,
      previousValue: previousStats.rainfall == null ? '—' : `${previousStats.rainfall.toFixed(1)} mm`,
      delta: buildDeltaMeta(currentStats.rainfall, previousStats.rainfall, { digits: 1, unit: 'mm', higherIsPositive: true }),
      narrative: currentStats.rainfall == null || previousStats.rainfall == null
        ? 'Ainda não há base suficiente para comparar a chuva acumulada entre as duas janelas.'
        : currentStats.rainfall > previousStats.rainfall
          ? 'A janela atual acumulou mais chuva do que a anterior equivalente.'
          : (currentStats.rainfall < previousStats.rainfall
            ? 'A janela atual acumulou menos chuva do que a anterior equivalente.'
            : 'A chuva acumulada das duas janelas ficou praticamente igual.'),
    },
    {
      id: 'pressure',
      title: 'Pressão média',
      accent: '🧭',
      value: currentStats.avgPressure == null ? '—' : `${currentStats.avgPressure.toFixed(1)} hPa`,
      previousValue: previousStats.avgPressure == null ? '—' : `${previousStats.avgPressure.toFixed(1)} hPa`,
      delta: buildDeltaMeta(currentStats.avgPressure, previousStats.avgPressure, { digits: 1, unit: 'hPa', higherIsPositive: true }),
      narrative: currentStats.avgPressure == null || previousStats.avgPressure == null
        ? 'A pressão média só aparece quando as duas janelas têm leituras válidas gravadas no banco climático.'
        : currentStats.avgPressure > previousStats.avgPressure
          ? 'A janela atual está com pressão média mais alta do que a anterior equivalente.'
          : (currentStats.avgPressure < previousStats.avgPressure
            ? 'A janela atual está com pressão média mais baixa do que a anterior equivalente.'
            : 'A pressão média das duas janelas ficou praticamente igual.'),
    },
  ];
  return {
    windowDays: safeWindow,
    enoughData,
    currentStats,
    previousStats,
    cards,
  };
}

function comparisonToneMeta(tone = 'neutral') {
  if (tone === 'positive') return { label: 'Mudança favorável', color: '#22C55E' };
  if (tone === 'attention') return { label: 'Mudança relevante', color: '#F59E0B' };
  return { label: 'Sem mudança forte', color: '#94A3B8' };
}

function historyPeriodOptions(items = []) {
  const total = Array.isArray(items) ? items.length : 0;
  const base = [7, 15, 30, 90, 180];
  return base.filter((days) => total >= Math.min(days, 7));
}

function normalizeHistoryDateValue(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
}

function countDaysBetween(start, end) {
  const startValue = normalizeHistoryDateValue(start);
  const endValue = normalizeHistoryDateValue(end);
  if (!startValue || !endValue) return null;
  const startDate = new Date(`${startValue}T00:00:00Z`);
  const endDate = new Date(`${endValue}T00:00:00Z`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null;
  return Math.round((endDate.getTime() - startDate.getTime()) / 86400000) + 1;
}

function buildHistoryRows(items = [], { days = 30, customRange = null } = {}) {
  const sourceItems = Array.isArray(items) ? items : [];
  const startDate = normalizeHistoryDateValue(customRange?.startDate);
  const endDate = normalizeHistoryDateValue(customRange?.endDate);
  const filteredItems = startDate && endDate
    ? sourceItems.filter((item) => {
        const observedOn = normalizeHistoryDateValue(item?.observed_on);
        return observedOn && observedOn >= startDate && observedOn <= endDate;
      })
    : sourceItems.slice(0, Math.max(1, Number(days) || 30));
  return filteredItems.map((item) => {
    const rain = Number(item?.rainfall_mm || 0);
    const tempMin = item?.temp_min_c == null || item?.temp_min_c === "" ? null : Number(item.temp_min_c);
    const tempMax = item?.temp_max_c == null || item?.temp_max_c === "" ? null : Number(item.temp_max_c);
    const humMin = item?.humidity_min_pct == null || item?.humidity_min_pct === "" ? null : Number(item.humidity_min_pct);
    const humMax = item?.humidity_max_pct == null || item?.humidity_max_pct === "" ? null : Number(item.humidity_max_pct);
    const pressure = item?.pressure_hpa == null || item?.pressure_hpa === "" ? null : Number(item.pressure_hpa);
    const avgTemp = tempMin == null && tempMax == null ? null : Number((((tempMin ?? tempMax ?? 0) + (tempMax ?? tempMin ?? 0)) / 2).toFixed(1));
    const avgHumidity = humMin == null && humMax == null ? null : Number((((humMin ?? humMax ?? 0) + (humMax ?? humMin ?? 0)) / 2).toFixed(0));
    return {
      id: String(item?.climate_id || item?.observed_on || Math.random()),
      observedOn: String(item?.observed_on || "").trim(),
      sourceLabel: String(item?.source_station_name || item?.source_station_id || item?.source_type || "Banco climático").trim(),
      rainLabel: `${rain.toFixed(1)} mm`,
      tempLabel: avgTemp == null ? "—" : `${avgTemp.toFixed(1)} °C`,
      tempRangeLabel: tempMin == null && tempMax == null ? "—" : `${tempMin == null ? '—' : tempMin.toFixed(1)} °C · ${tempMax == null ? '—' : tempMax.toFixed(1)} °C`,
      humidityLabel: avgHumidity == null ? "—" : `${avgHumidity.toFixed(0)}%`,
      humidityRangeLabel: humMin == null && humMax == null ? "—" : `${humMin == null ? '—' : humMin.toFixed(0)}% · ${humMax == null ? '—' : humMax.toFixed(0)}%`,
      pressureLabel: pressure == null ? "—" : `${pressure.toFixed(1)} hPa`,
      windLabel: item?.wind_avg_kmh == null || item?.wind_avg_kmh === "" ? "—" : `${Number(item.wind_avg_kmh).toFixed(1)} km/h`,
      notes: String(item?.notes || "").trim(),
      createdAt: String(item?.created_at || item?.updated_at || "").trim(),
      visual: buildClimateDailyVisualMeta({
        observed_on: item?.observed_on,
        temp_min_c: tempMin,
        temp_max_c: tempMax,
        rainfall_mm: rain,
        humidity_avg_pct: avgHumidity,
        humidity_max_pct: humMax,
        pressure_hpa: pressure,
        notes: item?.notes,
        summary: item?.summary,
        condition_label: item?.condition_label,
      }),
      raw: item,
    };
  });
}

function buildHistoryStats(rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  const count = list.length;
  if (!count) {
    return {
      days: 0,
      rainfallTotal: '0.0 mm',
      avgTemp: '—',
      avgHumidity: '—',
    };
  }
  const rainTotal = list.reduce((sum, row) => sum + (Number(row?.raw?.rainfall_mm || 0) || 0), 0);
  const temps = list.map((row) => row?.raw?.temp_min_c == null && row?.raw?.temp_max_c == null ? null : (((Number(row?.raw?.temp_min_c || row?.raw?.temp_max_c || 0) + Number(row?.raw?.temp_max_c || row?.raw?.temp_min_c || 0)) / 2))).filter((v) => Number.isFinite(v));
  const hums = list.map((row) => row?.raw?.humidity_min_pct == null && row?.raw?.humidity_max_pct == null ? null : (((Number(row?.raw?.humidity_min_pct || row?.raw?.humidity_max_pct || 0) + Number(row?.raw?.humidity_max_pct || row?.raw?.humidity_min_pct || 0)) / 2))).filter((v) => Number.isFinite(v));
  return {
    days: count,
    rainfallTotal: `${rainTotal.toFixed(1)} mm`,
    avgTemp: temps.length ? `${(temps.reduce((a,b)=>a+b,0)/temps.length).toFixed(1)} °C` : '—',
    avgHumidity: hums.length ? `${Math.round(hums.reduce((a,b)=>a+b,0)/hums.length)}%` : '—',
  };
}


function dedupePastureItems(items = [], farmId = "") {
  const pastureTs = (item) => Date.parse(item?.deletedAt || item?.updatedAt || item?.createdAt || "") || 0;
  const getBusinessKey = (item) => {
    if (!item || typeof item !== "object") return "";
    const fid = String(item.farmId || farmId || "").trim();
    const name = String(item.name || item.title || item.label || "").trim().toLowerCase();
    const costCenter = String(item.costCenter || "").trim().toLowerCase();
    if (!fid || !name) return "";
    return `pasture:biz:${fid}:${name}:${costCenter}`;
  };
  const completeness = (item) => {
    if (!item || typeof item !== "object") return 0;
    let score = 0;
    if (String(item.name || "").trim()) score += 5;
    if (String(item.costCenter || "").trim()) score += 3;
    if (String(item.grassType || "").trim()) score += 2;
    if (Array.isArray(item.polygon) && item.polygon.length) score += 6;
    if (Array.isArray(item.pois) && item.pois.length) score += 2;
    if (String(item.lat || "").trim()) score += 1;
    if (String(item.lng || "").trim()) score += 1;
    if (String(item.notes || "").trim()) score += 1;
    return score;
  };
  const choosePreferred = (prev, next) => {
    if (!prev) return next;
    if (!next) return prev;
    const prevDeleted = !!(prev?._deleted || prev?.deletedAt || prev?.deleted_at);
    const nextDeleted = !!(next?._deleted || next?.deletedAt || next?.deleted_at);
    const prevTs = pastureTs(prev);
    const nextTs = pastureTs(next);
    if (prevDeleted !== nextDeleted) {
      if (prevTs === nextTs) return nextDeleted ? next : prev;
      return nextTs > prevTs ? next : prev;
    }
    if (nextTs !== prevTs) return nextTs > prevTs ? next : prev;
    const prevScore = completeness(prev);
    const nextScore = completeness(next);
    if (nextScore !== prevScore) return nextScore > prevScore ? next : prev;
    const prevId = String(prev.clientId || prev.id || "");
    const nextId = String(next.clientId || next.id || "");
    return nextId >= prevId ? next : prev;
  };

  const byIdentity = new Map();
  const withoutIdentity = [];
  for (const raw of Array.isArray(items) ? items : []) {
    const item = raw && typeof raw === "object" ? raw : null;
    if (!item) continue;
    const directId = String(item.clientId || item.id || "").trim();
    const businessKey = getBusinessKey(item);
    const key = directId ? `id:${directId}` : businessKey;
    if (!key) {
      withoutIdentity.push(item);
      continue;
    }
    const preferred = choosePreferred(byIdentity.get(key), item);
    byIdentity.set(key, preferred);
    if (directId) byIdentity.set(`id:${directId}`, choosePreferred(byIdentity.get(`id:${directId}`), preferred));
    if (businessKey) byIdentity.set(businessKey, choosePreferred(byIdentity.get(businessKey), preferred));
  }

  const finalMap = new Map();
  for (const item of byIdentity.values()) {
    const finalKey = getBusinessKey(item) || `id:${String(item?.clientId || item?.id || '')}`;
    if (!finalKey) continue;
    finalMap.set(finalKey, choosePreferred(finalMap.get(finalKey), item));
  }
  for (const [index, item] of withoutIdentity.entries()) {
    finalMap.set(`loose:${index}:${String(item?.name || item?.title || '')}`, item);
  }
  return Array.from(finalMap.values());
}

function normalizeNumber(value, digits = 1) {
  const num = Number(value);
  return Number.isFinite(num) ? Number(num.toFixed(digits)) : null;
}

function statusMeta(status = "pending") {
  const key = String(status || "pending").trim() || "pending";
  if (key === "online") return { label: "Leitura em dia", color: "#22C55E" };
  if (key === "delayed") return { label: "Leitura em atraso", color: "#F59E0B" };
  if (key === "offline") return { label: "Estação offline", color: "#EF4444" };
  return { label: "Leitura pendente", color: "#94A3B8" };
}

function periodStatusMeta(status = "empty") {
  if (status === "ok") return { label: "Cobertura boa", color: "#22C55E" };
  if (status === "partial") return { label: "Dados parciais", color: "#F59E0B" };
  if (status === "limited") return { label: "Cobertura limitada", color: "#FB923C" };
  return { label: "Sem dados", color: "#94A3B8" };
}

function alertMeta(level = "info") {
  const key = String(level || "info").trim() || "info";
  if (key === "critical") return { label: "Crítico", color: "#EF4444" };
  if (key === "warning") return { label: "Atenção", color: "#F59E0B" };
  if (key === "ok") return { label: "Estável", color: "#22C55E" };
  return { label: "Informativo", color: "#60A5FA" };
}


function renderMetricValue(value, toneColor = null, options = {}) {
  const text = String(value ?? "—").trim();
  const baseColor = toneColor || options.color || "inherit";
  const unitColor = options.unitColor || baseColor;
  const numberSize = options.numberSize || 28;
  const unitSize = options.unitSize || 18;
  const unitWeight = options.unitWeight || 800;
  const gap = options.gap || 4;
  const match = text.match(/^(-?\d+(?:[.,]\d+)?)\s*(°C)$/i);
  if (!match) {
    return <span style={{ color: baseColor }}>{text}</span>;
  }
  return (
    <span style={{ display: "inline-flex", alignItems: "baseline", gap, flexWrap: "nowrap", whiteSpace: "nowrap" }}>
      <span style={{ color: baseColor, fontSize: numberSize, fontWeight: 900, lineHeight: 1 }}>{match[1]}</span>
      <span style={{ color: unitColor, fontSize: unitSize, fontWeight: unitWeight, lineHeight: 1 }}>{match[2]}</span>
    </span>
  );
}

function buildClimateCards(summary) {
  const latest = summary?.latest_snapshot || {};
  const humidityCurrent = normalizeNumber(latest?.humidity_current_pct, 0);
  const currentTemp = normalizeNumber(latest?.temp_current_c, 1);
  const maxTemp = normalizeNumber(latest?.temp_max_c, 1);
  const pressureHpa = normalizeNumber(latest?.pressure_hpa, 1);
  const observedOn = String(latest?.observed_on || summary?.latest_observed_on || "").trim();
  const observedLabel = observedOn ? fmtDate(observedOn) : "";
  const sourceLabel = String(summary?.source_station_name || summary?.source_station_id || "Estação principal").trim();
  const effectiveSource = String(summary?.effective_source || "manual").trim();
  const currentVisual = buildClimateVisualMeta(summary);
  const maxVisual = buildClimateMaxVisualMeta(summary);
  return [
    {
      id: "current_temp",
      title: "Temperatura Atual",
      value: currentTemp == null ? "Sem leitura" : `${currentTemp.toFixed(1)} °C`,
      accent: "🌡️",
      visual: currentVisual,
      helper: observedLabel ? `Leitura de ${observedLabel}` : "Última leitura indisponível",
      detail: currentTemp == null
        ? "Ainda não há leitura climática diária consolidada para mostrar a temperatura atual da estação principal."
        : `A leitura atual usa a estação ${sourceLabel}. O valor exibido é a melhor estimativa disponível para o dia, baseada no último snapshot diário gravado pelo Trembo. Fonte efetiva: ${effectiveSource.toUpperCase()}.`,
    },
    {
      id: "temp_max",
      title: "Máxima do Dia",
      value: maxTemp == null ? "Sem leitura" : `${maxTemp.toFixed(1)} °C`,
      accent: "☀️",
      visual: maxVisual,
      helper: observedLabel ? `Apurada em ${observedLabel}` : "Sem máximo diário consolidado",
      detail: maxTemp == null
        ? "O banco ainda não consolidou a máxima diária da estação principal para esta fazenda."
        : `A máxima diária considera a última leitura consolidada da estação ${sourceLabel}. Essa informação ajuda a perceber picos de calor que podem impactar o manejo no mesmo dia.`,
    },    {
      id: "humidity_current",
      title: "Umidade Atual",
      value: humidityCurrent == null ? "Sem leitura" : `${humidityCurrent.toFixed(0)}%`,
      accent: "💧",
      visual: null,
      helper: observedLabel ? `Leitura de ${observedLabel}` : "Última leitura indisponível",
      detail: humidityCurrent == null
        ? "Ainda não há leitura climática válida para mostrar a umidade atual da estação principal."
        : `A umidade atual usa a última leitura bruta válida da estação ${sourceLabel}. Fonte efetiva: ${effectiveSource.toUpperCase()}.`,
    },
    {
      id: "pressure_current",
      title: "Pressão",
      value: pressureHpa == null ? "Sem leitura" : `${pressureHpa.toFixed(1)} hPa`,
      accent: "🧭",
      visual: null,
      helper: observedLabel ? `Leitura de ${observedLabel}` : "Última leitura indisponível",
      detail: pressureHpa == null
        ? "Ainda não há leitura climática válida para mostrar a pressão atmosférica da estação principal."
        : `A pressão atmosférica usa a última leitura bruta válida da estação ${sourceLabel}. Fonte efetiva: ${effectiveSource.toUpperCase()}.`,
    },
  ];
}

function buildPeriodCards(periods = {}) {
  const order = ["daily", "weekly", "fortnightly", "monthly", "3months", "6months", "1year"];
  return order.map((key) => {
    const item = periods?.[key] || {};
    const avgTemp = normalizeNumber(item?.avg_temp_c, 1);
    const avgHumidity = normalizeNumber(item?.avg_humidity_pct, 0);
    const rainfall = normalizeNumber(item?.rainfall_mm, 1) ?? 0;
    const coverage = Number(item?.coverage_pct || 0);
    const daysWithData = Number(item?.days_with_data || 0);
    const expectedDays = Number(item?.days_expected || item?.window_days || 0);
    return {
      id: key,
      title: String(item?.label || key),
      accent: key === "daily" ? "📅" : key === "1year" ? "🗓️" : "🌦️",
      status: String(item?.status || "empty"),
      valuePrimary: avgTemp == null ? "Sem dados" : `${avgTemp.toFixed(1)} °C`,
      valueSecondary: avgHumidity == null ? "—" : `${avgHumidity.toFixed(0)}%`,
      rainfallLabel: `${rainfall.toFixed(1)} mm`,
      helper: daysWithData ? `${daysWithData}/${expectedDays} dia(s) com leitura · ${coverage}% de cobertura` : "Sem leituras consolidadas nesse período",
      detail: item,
    };
  });
}

function buildAlertCards(items = []) {
  return (Array.isArray(items) ? items : []).map((item, idx) => ({
    id: String(item?.id || item?.type || `alert_${idx}`),
    title: String(item?.title || item?.label || "Alerta climático"),
    level: String(item?.level || "info"),
    badge: String(item?.badge || item?.status_label || ""),
    icon: String(item?.icon || "⚠️"),
    summary: String(item?.summary || item?.message || ""),
    detail: String(item?.detail || item?.message || item?.summary || ""),
    observedOn: String(item?.observed_on || item?.last_seen_on || "").trim(),
    sourceLabel: String(item?.source_station_name || item?.source_station_id || "").trim(),
  }));
}


function planningLevelMeta(level = "attention") {
  if (level === "favorable") return { label: "Favorável", color: "#22C55E", icon: "✅" };
  if (level === "critical") return { label: "Crítica", color: "#EF4444", icon: "🛑" };
  if (level === "restrictive") return { label: "Restritiva", color: "#F97316", icon: "⚠️" };
  return { label: "Atenção", color: "#F59E0B", icon: "👀" };
}

function alertLevelScore(level = "info") {
  if (level === "critical") return 3;
  if (level === "warning" || level === "attention") return 2;
  if (level === "info") return 1;
  return 0;
}

function buildPlanningWindowLevel({ rainScore = 0, heatScore = 0, pastureScore = 0, dryScore = 0 } = {}) {
  const peak = Math.max(rainScore, heatScore, pastureScore, dryScore, 0);
  const highCount = [rainScore, heatScore, pastureScore, dryScore].filter((value) => value >= 3).length;
  if (peak >= 3 && highCount >= 2) return "critical";
  if (peak >= 3 || pastureScore >= 3) return "restrictive";
  if (peak >= 2) return "attention";
  return "favorable";
}

function buildPlanningSignalCards({ forecastSummary, activeAlertCards, recentAlertCards, climateCalc, impactLevel, trendMeta, comparisonSummary, monitoredPastures }) {
  const today = forecastSummary?.today || {};
  const days = Array.isArray(forecastSummary?.days) ? forecastSummary.days : [];
  const firstDays = days.slice(0, 2);
  const allDays = days.slice(0, 7);
  const activeLevels = (Array.isArray(activeAlertCards) ? activeAlertCards : []).map((item) => alertLevelScore(item?.level));
  const recentLevels = (Array.isArray(recentAlertCards) ? recentAlertCards : []).map((item) => alertLevelScore(item?.level));
  const activePeak = activeLevels.length ? Math.max(...activeLevels) : 0;
  const recentPeak = recentLevels.length ? Math.max(...recentLevels) : 0;

  const todayRainChance = Number(today?.rain_chance_pct || 0);
  const todayRainMm = Number(today?.rainfall_mm || 0);
  const next48RainChance = firstDays.length ? Math.max(...firstDays.map((item) => Number(item?.rain_chance_pct || 0))) : todayRainChance;
  const next48RainMm = firstDays.length ? firstDays.reduce((sum, item) => sum + Number(item?.rainfall_mm || 0), 0) : todayRainMm;
  const weekRainMm = allDays.length ? allDays.reduce((sum, item) => sum + Number(item?.rainfall_mm || 0), 0) : todayRainMm;

  const todayMaxTemp = Number(today?.temp_max_c || 0);
  const next48MaxTemp = firstDays.length ? Math.max(...firstDays.map((item) => Number(item?.temp_max_c || 0))) : todayMaxTemp;
  const weekMaxTemp = allDays.length ? Math.max(...allDays.map((item) => Number(item?.temp_max_c || 0))) : todayMaxTemp;

  const dryDays = Number(climateCalc?.dryDays || 0);
  const recentRainfall = Number(climateCalc?.rainfallMm || 0);
  const comparisonRainCurrent = Number(comparisonSummary?.currentStats?.rainfall || 0);
  const comparisonRainPrevious = Number(comparisonSummary?.previousStats?.rainfall || 0);

  const rainTodayScore = activePeak >= 3 ? 3 : ((todayRainChance >= 75 || todayRainMm >= 20) ? 3 : ((todayRainChance >= 45 || todayRainMm >= 8 || recentPeak >= 2) ? 2 : 1));
  const rain48Score = activePeak >= 3 ? 3 : ((next48RainChance >= 75 || next48RainMm >= 25) ? 3 : ((next48RainChance >= 45 || next48RainMm >= 10) ? 2 : 1));
  const rainWeekScore = weekRainMm >= 50 ? 2 : 1;

  const heatTodayScore = activePeak >= 3 && todayMaxTemp >= 34 ? 3 : (todayMaxTemp >= 35 ? 3 : (todayMaxTemp >= 31 ? 2 : 1));
  const heat48Score = next48MaxTemp >= 35 ? 3 : (next48MaxTemp >= 31 ? 2 : 1);
  const heatWeekScore = weekMaxTemp >= 35 ? 3 : (weekMaxTemp >= 31 ? 2 : 1);

  const dryBaseScore = dryDays >= 18 ? 3 : (dryDays >= 10 ? 2 : 1);
  const dryTrendPenalty = comparisonSummary?.enoughData && comparisonRainCurrent < comparisonRainPrevious ? 1 : 0;
  const dryScore = Math.min(3, dryBaseScore + dryTrendPenalty + (recentRainfall <= 5 ? 1 : 0));
  const pastureScore = impactLevel === "critical" ? 3 : (impactLevel === "restrictive" ? 3 : (impactLevel === "attention" ? 2 : 1));

  const windows = [
    {
      id: 'today',
      title: 'Janela imediata',
      subtitle: 'Hoje',
      level: buildPlanningWindowLevel({ rainScore: rainTodayScore, heatScore: heatTodayScore, pastureScore, dryScore }),
      narrative: todayRainChance >= 45 || todayRainMm >= 8
        ? 'Há risco climático relevante no curto prazo, especialmente por chuva prevista ou pista molhada.'
        : (todayMaxTemp >= 31 ? 'O calor pede atenção na execução de atividades mais expostas hoje.' : 'A janela imediata está mais confortável para operação leve e observação de campo.'),
      helper: `Chuva ${todayRainChance ? `${todayRainChance.toFixed(0)}%` : '—'} · máx ${todayMaxTemp ? `${todayMaxTemp.toFixed(1)} °C` : '—'}`,
    },
    {
      id: 'next48',
      title: 'Próximas 48h',
      subtitle: 'Curto prazo',
      level: buildPlanningWindowLevel({ rainScore: rain48Score, heatScore: heat48Score, pastureScore, dryScore }),
      narrative: next48RainChance >= 45 || next48RainMm >= 10
        ? 'A previsão de 48h sugere organizar tarefas externas em janelas menores e checar chance de interrupção por chuva.'
        : (next48MaxTemp >= 31 ? 'O calor segue como principal ponto de atenção nas próximas 48h.' : 'As próximas 48h tendem a ser mais estáveis para rotinas operacionais.'),
      helper: `Chuva acumulada ${next48RainMm.toFixed(1)} mm · pico ${next48MaxTemp ? `${next48MaxTemp.toFixed(1)} °C` : '—'}`,
    },
    {
      id: 'week',
      title: 'Próximos dias',
      subtitle: 'Tendência curta',
      level: buildPlanningWindowLevel({ rainScore: rainWeekScore, heatScore: heatWeekScore, pastureScore, dryScore }),
      narrative: dryScore >= 3
        ? 'A tendência climática continua pressionando umidade e suporte de pastagens, então convém manter manejo conservador.'
        : (weekRainMm >= 30 ? 'Existe chance de ganho hídrico nos próximos dias, o que pode abrir janelas melhores depois da chuva.' : 'A tendência dos próximos dias não mostra alívio forte e pede acompanhamento contínuo do clima.'),
      helper: `Chuva prevista ${weekRainMm.toFixed(1)} mm · dias secos ${Math.round(dryDays)}`,
    },
  ];

  const rainSignal = {
    id: 'rain',
    title: 'Risco de chuva',
    icon: '🌧️',
    level: rain48Score >= 3 ? 'restrictive' : (rain48Score >= 2 ? 'attention' : 'favorable'),
    value: next48RainChance ? `${next48RainChance.toFixed(0)}%` : 'Baixo',
    narrative: next48RainChance >= 75 || next48RainMm >= 25
      ? 'Existe risco forte de chuva no curto prazo. Vale evitar comprometer tarefas que dependem de janela seca.'
      : (next48RainChance >= 45 || next48RainMm >= 10
        ? 'Há possibilidade relevante de chuva nas próximas 48h. Vale preparar plano B para campo e deslocamentos.'
        : 'A previsão de chuva do curto prazo está mais leve, favorecendo organização de tarefas externas.'),
  };
  const heatSignal = {
    id: 'heat',
    title: 'Tendência de calor',
    icon: '🥵',
    level: heat48Score >= 3 ? 'restrictive' : (heat48Score >= 2 ? 'attention' : 'favorable'),
    value: next48MaxTemp ? `${next48MaxTemp.toFixed(1)} °C` : 'Sem pico',
    narrative: next48MaxTemp >= 35
      ? 'Calor forte previsto. Concentre tarefas mais pesadas em horários mais amenos e revise água/sombra.'
      : (next48MaxTemp >= 31
        ? 'O calor merece atenção nas próximas 48h, principalmente em rotinas de campo prolongadas.'
        : 'Não há sinal de calor excessivo relevante no curto prazo.'),
  };
  const drySignal = {
    id: 'dry',
    title: 'Tendência de seca',
    icon: '☀️',
    level: dryScore >= 3 ? 'restrictive' : (dryScore >= 2 ? 'attention' : 'favorable'),
    value: `${Math.round(dryDays)} dia(s)`,
    narrative: dryScore >= 3
      ? 'A sequência seca já pressiona a janela operacional e pede mais cautela com uso das áreas e manejo hídrico.'
      : (dryScore >= 2
        ? 'A seca ainda não é extrema, mas merece monitoramento para evitar piora do suporte das áreas.'
        : 'A sequência seca está sob controle na leitura atual.'),
  };
  const handlingSignal = {
    id: 'handling',
    title: 'Atenção para manejo',
    icon: '🌱',
    level: impactLevel === 'critical' ? 'critical' : (impactLevel === 'restrictive' ? 'restrictive' : (impactLevel === 'attention' ? 'attention' : 'favorable')),
    value: monitoredPastures ? `${monitoredPastures} área(s)` : 'Sem áreas',
    narrative: monitoredPastures
      ? (trendMeta?.direction === 'worsening'
          ? 'A leitura climática das pastagens está piorando. Vale revisar permanência dos lotes e ritmo de uso das áreas.'
          : (trendMeta?.direction === 'improving'
              ? 'A leitura das pastagens está melhorando, mas ainda convém seguir com manejo medido e observação frequente.'
              : 'O manejo pode seguir, mas com monitoramento da condição climática e da resposta das áreas.'))
      : 'Ainda não há pastagens monitoradas suficientes para uma orientação operacional mais específica.',
  };

  const overallLevel = windows.reduce((acc, item) => {
    const order = { favorable: 0, attention: 1, restrictive: 2, critical: 3 };
    return order[item.level] > order[acc] ? item.level : acc;
  }, 'favorable');
  const recommendationList = [
    rain48Score >= 2 ? 'Reservar uma janela seca antes de tarefas externas mais sensíveis.' : 'Aproveitar a janela mais estável para organizar rotinas externas.',
    heat48Score >= 2 ? 'Concentrar atividades mais pesadas em horários menos quentes e revisar água/sombra.' : 'O calor não aparece como travador principal no curto prazo.',
    dryScore >= 2 ? 'Acompanhar suporte hídrico e sinais de pressão nas áreas mais usadas.' : 'Sem pressão seca forte nesta leitura.',
    pastureScore >= 2 ? 'Revisar lotação, giro e resposta das áreas antes de avançar manejo.' : 'Manejo das áreas pode seguir com acompanhamento normal.',
  ];

  return {
    overallLevel,
    overallNarrative: overallLevel === 'critical'
      ? 'O clima virou fator forte de restrição operacional no curto prazo. Vale trabalhar com plano conservador.'
      : (overallLevel === 'restrictive'
        ? 'A operação segue possível, mas com travas importantes de clima nas próximas janelas.'
        : (overallLevel === 'attention'
          ? 'O clima pede atenção pontual no curto prazo, sem travar toda a operação.'
          : 'A leitura climática atual está mais favorável para o dia a dia da fazenda.')),
    windows,
    signals: [rainSignal, heatSignal, drySignal, handlingSignal],
    recommendationList,
  };
}

function forecastStatusMeta(status = "empty") {
  if (status === "ok") return { label: "Previsão disponível", color: "#22C55E" };
  if (status === "partial") return { label: "Previsão parcial", color: "#F59E0B" };
  if (status === "stale") return { label: "Previsão desatualizada", color: "#FB923C" };
  return { label: "Sem previsão", color: "#94A3B8" };
}

function buildForecastCards(forecast) {
  const items = Array.isArray(forecast?.days) ? forecast.days : [];
  return items.map((item, idx) => ({
    id: String(item?.id || item?.date || `forecast_${idx}`),
    title: String(item?.label || item?.day_label || item?.date_label || `Dia ${idx + 1}`),
    icon: String(item?.icon || "🌤️"),
    visual: buildForecastVisualMeta(item),
    condition: String(item?.condition_label || item?.condition || "Sem previsão"),
    minLabel: item?.temp_min_c == null ? "—" : `${Number(item.temp_min_c).toFixed(1)} °C`,
    maxLabel: item?.temp_max_c == null ? "—" : `${Number(item.temp_max_c).toFixed(1)} °C`,
    rainChanceLabel: item?.rain_chance_pct == null ? "—" : `${Number(item.rain_chance_pct).toFixed(0)}%`,
    rainfallLabel: item?.rainfall_mm == null ? "—" : `${Number(item.rainfall_mm).toFixed(1)} mm`,
    detail: item,
  }));
}

function formatPeriodRange(fmtDate, period) {
  const start = String(period?.period_start || "").trim();
  const end = String(period?.period_end || "").trim();
  if (!start && !end) return "Sem intervalo consolidado";
  if (start && end) return `${fmtDate(start)} até ${fmtDate(end)}`;
  return fmtDate(start || end);
}

function StatusChip({ color, label }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 999, padding: "7px 10px", fontSize: 12, fontWeight: 700, background: `${color}22`, color, border: `1px solid ${color}55` }}>
      <span style={{ width: 8, height: 8, borderRadius: 999, background: color }} />
      {label}
    </div>
  );
}

export function createClimateModule(deps) {
  const {
    useState,
    useEffect,
    useMemo,
    useCallback,
    useFarm,
    useFarmCollection,
    useGlobalCollection,
    useToast,
    ModuleHeaderMenu,
    Btn,
    Badge,
    Toast,
    T,
    fmtDate,
  } = deps;

  function ClimateModule({ session }) {
    const { fid } = useFarm();
    const [adminGlobalsConfigRows] = useGlobalCollection("adminGlobalsConfig", [normalizeAdminGlobalsConfig(null)]);
    const adminGlobalsConfig = useMemo(() => normalizeAdminGlobalsConfig((adminGlobalsConfigRows || [])[0]), [adminGlobalsConfigRows]);
    const climateProfiles = useMemo(() => adminGlobalsConfig?.farmClimateProfiles || {}, [adminGlobalsConfig]);
    const climateGlobalProfile = useMemo(() => adminGlobalsConfig?.climateGlobalProfile || {}, [adminGlobalsConfig]);
    const farmClimateProfile = useMemo(() => getFarmClimateProfile(climateProfiles, fid), [climateProfiles, fid]);
    const stationCandidates = useMemo(() => normalizeClimateStations(farmClimateProfile?.stations, farmClimateProfile), [farmClimateProfile]);

    const [summary, setSummary] = useState(null);
    const [pastures] = useFarmCollection("pastures", []);
    const [periodSummary, setPeriodSummary] = useState({ periods: {} });
    const [alertsSummary, setAlertsSummary] = useState({ active_alerts: [], recent_alerts: [] });
    const [forecastSummary, setForecastSummary] = useState({ status: { key: "empty" }, today: null, days: [] });
    const [historyRows, setHistoryRows] = useState([]);
    const [comparisonDays, setComparisonDays] = useState(30);
    const [historyPeriodDays, setHistoryPeriodDays] = useState(30);
    const [historyFilterMode, setHistoryFilterMode] = useState("preset");
    const [historyCustomStart, setHistoryCustomStart] = useState("");
    const [historyCustomEnd, setHistoryCustomEnd] = useState("");
    const [historyFilterError, setHistoryFilterError] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingPeriods, setLoadingPeriods] = useState(false);
    const [loadingAlerts, setLoadingAlerts] = useState(false);
    const [loadingForecast, setLoadingForecast] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [expandedCard, setExpandedCard] = useState("");
    const [activeView, setActiveView] = useState("home");
    const [toast, show] = useToast();

    const requestBase = useMemo(() => ({
      farmId: fid,
      preferredSource: farmClimateProfile?.preferredSource || climateGlobalProfile?.preferredSource || "wunderground",
      fallbackToInmet: farmClimateProfile?.fallbackToInmet !== false,
      stationCandidates,
      alertRules: {
        heat_enabled: farmClimateProfile?.alertsHeatEnabled,
        heat_warning_temp_c: farmClimateProfile?.alertsHeatWarningTempC,
        heat_critical_temp_c: farmClimateProfile?.alertsHeatCriticalTempC,
        low_humidity_enabled: farmClimateProfile?.alertsLowHumidityEnabled,
        low_humidity_warning_pct: farmClimateProfile?.alertsLowHumidityWarningPct,
        low_humidity_critical_pct: farmClimateProfile?.alertsLowHumidityCriticalPct,
        heavy_rain_enabled: farmClimateProfile?.alertsHeavyRainEnabled,
        heavy_rain_warning_mm: farmClimateProfile?.alertsHeavyRainWarningMm,
        heavy_rain_critical_mm: farmClimateProfile?.alertsHeavyRainCriticalMm,
        dry_streak_enabled: farmClimateProfile?.alertsDryStreakEnabled,
        dry_streak_warning_days: farmClimateProfile?.alertsDryStreakWarningDays,
        dry_streak_critical_days: farmClimateProfile?.alertsDryStreakCriticalDays,
      },
      userName: session?.name || "",
    }), [fid, farmClimateProfile?.preferredSource, farmClimateProfile?.fallbackToInmet, farmClimateProfile?.alertsHeatEnabled, farmClimateProfile?.alertsHeatWarningTempC, farmClimateProfile?.alertsHeatCriticalTempC, farmClimateProfile?.alertsLowHumidityEnabled, farmClimateProfile?.alertsLowHumidityWarningPct, farmClimateProfile?.alertsLowHumidityCriticalPct, farmClimateProfile?.alertsHeavyRainEnabled, farmClimateProfile?.alertsHeavyRainWarningMm, farmClimateProfile?.alertsHeavyRainCriticalMm, farmClimateProfile?.alertsDryStreakEnabled, farmClimateProfile?.alertsDryStreakWarningDays, farmClimateProfile?.alertsDryStreakCriticalDays, climateGlobalProfile?.preferredSource, stationCandidates, session?.name]);

    const loadSummary = useCallback(async ({ silent = false, autoSync = false } = {}) => {
      if (!fid) {
        setSummary(null);
        return null;
      }
      if (!silent) setLoading(true);
      try {
        const startedAt = Date.now();
        const data = await fetchFarmClimateSummary({ ...requestBase, autoSync });
        setSummary(data || null);
        appendClimateTelemetry({ type: 'load_summary', farmId: fid, view: activeView, status: 'success', sourceLabel: String(data?.source_station_name || data?.source_station_id || ''), durationMs: Date.now() - startedAt });
        return data || null;
      } catch (error) {
        appendClimateTelemetry({ type: 'load_summary', farmId: fid, view: activeView, status: 'error', message: error?.message || 'summary_error' });
        setSummary(null);
        show(error?.message || "Não foi possível carregar o clima da fazenda.", "error");
        return null;
      } finally {
        if (!silent) setLoading(false);
      }
    }, [fid, requestBase, activeView, show]);

    const loadPeriodSummaries = useCallback(async ({ silent = false, autoSync = false } = {}) => {
      if (!fid) {
        setPeriodSummary({ periods: {} });
        return null;
      }
      if (!silent) setLoadingPeriods(true);
      try {
        const startedAt = Date.now();
        const data = await fetchClimatePeriodSummaries({ ...requestBase, autoSync });
        setPeriodSummary(data || { periods: {} });
        appendClimateTelemetry({ type: 'load_periods', farmId: fid, view: activeView, status: 'success', durationMs: Date.now() - startedAt });
        return data || null;
      } catch (error) {
        appendClimateTelemetry({ type: 'load_periods', farmId: fid, view: activeView, status: 'error', message: error?.message || 'periods_error' });
        setPeriodSummary({ periods: {} });
        show(error?.message || "Não foi possível carregar os resumos climáticos.", "error");
        return null;
      } finally {
        if (!silent) setLoadingPeriods(false);
      }
    }, [fid, requestBase, activeView, show]);

    const loadForecast = useCallback(async ({ silent = false, autoSync = false } = {}) => {
      if (!fid) {
        setForecastSummary({ status: { key: "empty" }, today: null, days: [] });
        return null;
      }
      if (!silent) setLoadingForecast(true);
      try {
        const startedAt = Date.now();
        const data = await fetchClimateForecastBase({ ...requestBase, autoSync });
        setForecastSummary(data || { status: { key: "empty" }, today: null, days: [] });
        appendClimateTelemetry({ type: 'load_forecast', farmId: fid, view: activeView, status: 'success', durationMs: Date.now() - startedAt });
        return data || null;
      } catch (error) {
        appendClimateTelemetry({ type: 'load_forecast', farmId: fid, view: activeView, status: 'error', message: error?.message || 'forecast_error' });
        setForecastSummary({ status: { key: "empty" }, today: null, days: [] });
        show(error?.message || "Não foi possível carregar a previsão do tempo.", "error");
        return null;
      } finally {
        if (!silent) setLoadingForecast(false);
      }
    }, [fid, requestBase, activeView, show]);

    const loadHistory = useCallback(async ({ silent = false, limit = 365 } = {}) => {
      if (!fid) {
        setHistoryRows([]);
        return null;
      }
      if (!silent) setLoadingHistory(true);
      try {
        const startedAt = Date.now();
        const items = await fetchClimateDailyHistory({ farmId: fid, limit, userName: session?.name || "" });
        setHistoryRows(Array.isArray(items) ? items : []);
        appendClimateTelemetry({ type: 'load_history', farmId: fid, view: activeView, status: 'success', durationMs: Date.now() - startedAt, details: { limit } });
        return items || [];
      } catch (error) {
        appendClimateTelemetry({ type: 'load_history', farmId: fid, view: activeView, status: 'error', message: error?.message || 'history_error', details: { limit } });
        setHistoryRows([]);
        show(error?.message || "Não foi possível carregar o histórico climático.", "error");
        return null;
      } finally {
        if (!silent) setLoadingHistory(false);
      }
    }, [fid, session?.name, activeView, show]);

    const loadAlerts = useCallback(async ({ silent = false, autoSync = false } = {}) => {
      if (!fid) {
        setAlertsSummary({ active_alerts: [], recent_alerts: [] });
        return null;
      }
      if (!silent) setLoadingAlerts(true);
      try {
        const startedAt = Date.now();
        const data = await fetchClimateAlertsBase({ ...requestBase, autoSync });
        setAlertsSummary(data || { active_alerts: [], recent_alerts: [] });
        appendClimateTelemetry({ type: 'load_alerts', farmId: fid, view: activeView, status: 'success', durationMs: Date.now() - startedAt });
        return data || null;
      } catch (error) {
        appendClimateTelemetry({ type: 'load_alerts', farmId: fid, view: activeView, status: 'error', message: error?.message || 'alerts_error' });
        setAlertsSummary({ active_alerts: [], recent_alerts: [] });
        show(error?.message || "Não foi possível carregar os alertas climáticos.", "error");
        return null;
      } finally {
        if (!silent) setLoadingAlerts(false);
      }
    }, [fid, requestBase, activeView, show]);

    useEffect(() => {
      loadSummary({ autoSync: true });
      loadPeriodSummaries({ silent: true, autoSync: false });
      loadAlerts({ silent: true, autoSync: false });
      loadForecast({ silent: true, autoSync: false });
      loadHistory({ silent: true, limit: 120 });
    }, [loadSummary, loadPeriodSummaries, loadAlerts, loadForecast, loadHistory]);

    useEffect(() => {
      if (!fid) return;
      appendClimateTelemetry({ type: 'view_open', farmId: fid, view: activeView, status: 'ok' });
    }, [fid, activeView]);

    useEffect(() => {
      if ((activeView === 'history' || activeView === 'comparisons' || activeView === 'consolidation') && historyRows.length > 0 && historyRows.length < 365) {
        loadHistory({ silent: true, limit: 365 });
      }
    }, [activeView, historyRows.length, loadHistory]);

    const stationCount = stationCandidates.length;
    const latestSnapshot = summary?.latest_snapshot || null;
    const observedOn = String(latestSnapshot?.observed_on || summary?.latest_observed_on || "").trim();
    const sourceLabel = String(summary?.source_station_name || summary?.source_station_id || periodSummary?.source_station_name || periodSummary?.source_station_id || alertsSummary?.source_station_name || alertsSummary?.source_station_id || "").trim();
    const sourceStatus = statusMeta(summary?.integration_status);
    const autoSyncWindow = summary?.auto_sync_window || {};
    const syncWindowMessage = autoSyncWindow?.should_defer_auto_sync
      ? (autoSyncWindow?.reason === "cron_running"
          ? "O cron climático está processando novas leituras agora. O módulo aguarda a conclusão antes de tentar nova consulta automática."
          : "O cron climático acabou de concluir. O módulo respeita uma janela de segurança de 5 minutos antes de tentar nova atualização automática.")
      : "As leituras automáticas do módulo seguem a mesma janela operacional do cron climático.";
    const cards = useMemo(() => buildClimateCards(summary), [summary]);
    const heroVisual = useMemo(() => buildClimateVisualMeta(summary), [summary]);
    const periodCards = useMemo(() => buildPeriodCards(periodSummary?.periods || {}), [periodSummary]);
    const activeAlertCards = useMemo(() => buildAlertCards(alertsSummary?.active_alerts || []), [alertsSummary]);
    const recentAlertCards = useMemo(() => buildAlertCards(alertsSummary?.recent_alerts || []), [alertsSummary]);
    const forecastCards = useMemo(() => buildForecastCards(forecastSummary), [forecastSummary]);
    const overallAlertMeta = alertMeta(alertsSummary?.overall_level || "info");
    const forecastMeta = forecastStatusMeta(forecastSummary?.status?.key || "empty");
    const historyOptions = useMemo(() => historyPeriodOptions(historyRows), [historyRows]);
    const selectedHistoryDays = historyOptions.includes(historyPeriodDays) ? historyPeriodDays : (historyOptions[0] || 30);
    const historyCustomRange = useMemo(() => {
      const startDate = normalizeHistoryDateValue(historyCustomStart);
      const endDate = normalizeHistoryDateValue(historyCustomEnd);
      if (historyFilterMode !== "custom" || !startDate || !endDate) return null;
      return { startDate, endDate };
    }, [historyFilterMode, historyCustomStart, historyCustomEnd]);
    const historyCustomDayCount = useMemo(() => countDaysBetween(historyCustomRange?.startDate, historyCustomRange?.endDate), [historyCustomRange]);
    const visibleHistoryRows = useMemo(() => buildHistoryRows(historyRows, historyCustomRange ? { customRange: historyCustomRange } : { days: selectedHistoryDays }), [historyRows, historyCustomRange, selectedHistoryDays]);
    const historyStats = useMemo(() => buildHistoryStats(visibleHistoryRows), [visibleHistoryRows]);
    const comparisonSummary = useMemo(() => buildComparisonCards(historyRows, comparisonDays), [historyRows, comparisonDays]);
    const historyAppliedLabel = historyCustomRange
      ? `Período: ${fmtDate(historyCustomRange.startDate)} a ${fmtDate(historyCustomRange.endDate)}`
      : `Período: últimos ${selectedHistoryDays} dias`;
    const hasReading = !!observedOn && !!latestSnapshot;
    const hasPeriodData = periodCards.some((card) => !!card?.detail?.has_data);
    const visiblePastures = useMemo(() => dedupePastureItems((Array.isArray(pastures) ? pastures : []).filter((item) => item && !isDeletedItem(item)), fid), [pastures, fid]);
    const monitoredPastures = useMemo(() => visiblePastures.filter((item) => item?.useAnimalCapacity !== false), [visiblePastures]);
    const normalizedClimateSummary = useMemo(() => normalizeClimateDatabaseSummary(summary), [summary]);
    const climateCalc = useMemo(() => computeClimateCapacityAdjustment(farmClimateProfile, normalizedClimateSummary), [farmClimateProfile, normalizedClimateSummary]);
    const climateCalc15 = useMemo(() => computeClimateCapacityAdjustment({ ...farmClimateProfile, analysisWindowDays: 15 }, buildClimateWindowSummary(normalizedClimateSummary, 15)), [farmClimateProfile, normalizedClimateSummary]);
    const climateCalc30 = useMemo(() => computeClimateCapacityAdjustment({ ...farmClimateProfile, analysisWindowDays: 30 }, buildClimateWindowSummary(normalizedClimateSummary, 30)), [farmClimateProfile, normalizedClimateSummary]);
    const climateCalc90 = useMemo(() => computeClimateCapacityAdjustment({ ...farmClimateProfile, analysisWindowDays: 90 }, buildClimateWindowSummary(normalizedClimateSummary, 90)), [farmClimateProfile, normalizedClimateSummary]);
    const trendMeta = useMemo(() => buildTrendMeta(climateCalc, climateCalc15, climateCalc30, climateCalc90), [climateCalc, climateCalc15, climateCalc30, climateCalc90]);
    const pastureBaseUaTotal = useMemo(() => monitoredPastures.reduce((sum, item) => sum + toPositiveNumber(computePastureCapacity(item, adminGlobalsConfig?.pastureCapacityConfig)?.uaTotal, 0), 0), [monitoredPastures, adminGlobalsConfig]);
    const pastureAdjustedUaTotal = useMemo(() => Number((pastureBaseUaTotal * Number(climateCalc?.factor || 1)).toFixed(2)), [pastureBaseUaTotal, climateCalc]);
    const impactLevel = useMemo(() => resolvePastureImpactLevel(climateCalc, !!normalizedClimateSummary?.latestObservedOn), [climateCalc, normalizedClimateSummary]);
    const impactMeta = useMemo(() => getImpactMeta(impactLevel), [impactLevel]);
    const impactNarrative = useMemo(() => buildPastureImpactNarrative(impactLevel, climateCalc, monitoredPastures.length), [impactLevel, climateCalc, monitoredPastures]);
    const impactGuidance = useMemo(() => buildPastureImpactGuidance(impactLevel, trendMeta.direction), [impactLevel, trendMeta]);
    const impactFactors = useMemo(() => buildImpactFactorRows({ normalizedSummary: normalizedClimateSummary, climateCalc, trendMeta, monitoredPastures: monitoredPastures.length, baseUaTotal: pastureBaseUaTotal, adjustedUaTotal: pastureAdjustedUaTotal, sourceLabel, fmtDate }), [normalizedClimateSummary, climateCalc, trendMeta, monitoredPastures.length, pastureBaseUaTotal, pastureAdjustedUaTotal, sourceLabel]);
    const planningSummary = useMemo(() => buildPlanningSignalCards({ forecastSummary, activeAlertCards, recentAlertCards, climateCalc, impactLevel, trendMeta, comparisonSummary, monitoredPastures: monitoredPastures.length }), [forecastSummary, activeAlertCards, recentAlertCards, climateCalc, impactLevel, trendMeta, comparisonSummary, monitoredPastures.length]);

    const emptyState = !stationCount
      ? {
          title: "Sem estação configurada",
          body: "Cadastre pelo menos uma estação climática para a fazenda no Admin > Clima. Assim o módulo Clima passa a mostrar dados reais da estação principal.",
          action: "Atualizar agora",
        }
      : !hasReading
        ? {
            title: "Sem leitura climática consolidada",
            body: "A fazenda já tem estação configurada, mas ainda não existe leitura diária consolidada no banco para montar os cards iniciais do módulo.",
            action: "Tentar recarregar",
          }
        : (summary?.integration_status === "delayed" || summary?.integration_status === "offline")
          ? {
              title: "Leitura atrasada",
              body: "A estação principal existe, mas a última leitura disponível está atrasada para uso confortável no painel inicial do módulo Clima.",
              action: "Atualizar agora",
            }
          : null;

    return (
      <div>
        <ModuleHeaderMenu
          T={T}
          title="Clima"
          items={[
            { id: "home", label: "Início", icon: "🌦️", active: activeView === "home", onClick: () => setActiveView("home") },
            { id: "summaries", label: "Resumos Climáticos", icon: "🧾", active: activeView === "summaries", onClick: () => setActiveView("summaries") },
            { id: "history", label: "Histórico Climático", icon: "🗂️", active: activeView === "history", onClick: () => setActiveView("history") },
            { id: "comparisons", label: "Comparativos", icon: "🆚", active: activeView === "comparisons", onClick: () => setActiveView("comparisons") },
            { id: "alerts", label: "Alertas Climáticos", icon: "🚨", active: activeView === "alerts", onClick: () => setActiveView("alerts") },
            { id: "forecast", label: "Previsão do Tempo", icon: "🔮", active: activeView === "forecast", onClick: () => setActiveView("forecast") },
            { id: "pasture_impact", label: "Impacto em Pastagens", icon: "🌱", active: activeView === "pasture_impact", onClick: () => setActiveView("pasture_impact") },
            { id: "planning", label: "Planejamento Operacional", icon: "🧭", active: activeView === "planning", onClick: () => setActiveView("planning") },
          ]}
        />

        {activeView === "home" && (
          <div style={{ display: "grid", gap: 14 }}>
            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 18, padding: 16, display: "grid", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>Clima</div>
                  <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
                    Página inicial do módulo com leitura rápida da estação principal da fazenda.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <Badge>{stationCount ? `${stationCount} estação${stationCount > 1 ? "ões" : ""}` : "Sem estação"}</Badge>
                  <StatusChip color={sourceStatus.color} label={sourceStatus.label} />
                  <Btn size="sm" variant="ghost" onClick={() => { loadSummary({ autoSync: true }); loadPeriodSummaries({ silent: true, autoSync: false }); loadAlerts({ silent: true, autoSync: false }); loadForecast({ silent: true, autoSync: false }); }} disabled={loading}>{loading ? "Atualizando..." : "Atualizar"}</Btn>
                </div>
              </div>

              <div style={{ display: "grid", gap: 8, color: T.muted, fontSize: 13 }}>
                <div><strong style={{ color: T.text }}>Estação principal:</strong> {sourceLabel || "Ainda não definida"}</div>
                <div><strong style={{ color: T.text }}>Última leitura:</strong> {observedOn ? fmtDate(observedOn) : "Sem leitura no banco"}</div>
                <div><strong style={{ color: T.text }}>Resumo:</strong> {String(summary?.summary || "Integração climática pendente.")}</div>
                <div><strong style={{ color: T.text }}>Janela de atualização:</strong> {syncWindowMessage}</div>
              </div>
              {heroVisual?.src ? (
                <div style={{ display:"grid", gridTemplateColumns:"minmax(0,1fr)", justifyItems:"center", gap:10, padding:"6px 0 2px" }}>
                  <div style={{ width:"100%", maxWidth:300, minHeight:132, borderRadius:20, border:`1px solid ${T.border}`, background:T.card2, display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden", padding:12 }}>
                    <img src={heroVisual.src} alt={heroVisual.label || "Leitura climática"} style={{ width:"100%", height:150, objectFit:"contain" }} />
                  </div>
                  <div style={{ fontSize:12, color:T.muted, textAlign:"center" }}>{heroVisual.label || "Condição atual"}{heroVisual.observedLabel ? ` · leitura ${heroVisual.observedLabel}` : ""}</div>
                </div>
              ) : null}
            </div>

            {emptyState ? (
              <div style={{ background: T.card, border: `1px dashed ${T.border}`, borderRadius: 18, padding: 18, display: "grid", gap: 10 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>{emptyState.title}</div>
                <div style={{ fontSize: 14, color: T.muted, lineHeight: 1.5 }}>{emptyState.body}</div>
                <div>
                  <Btn onClick={() => { loadSummary({ autoSync: true }); loadPeriodSummaries({ silent: true, autoSync: false }); loadAlerts({ silent: true, autoSync: false }); loadForecast({ silent: true, autoSync: false }); }} disabled={loading}>{loading ? "Carregando..." : emptyState.action}</Btn>
                </div>
              </div>
            ) : (
              <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
                {cards.map((card) => {
                  const open = expandedCard === card.id;
                  return (
                    <div
                      key={card.id}
                      onClick={() => setExpandedCard((prev) => (prev === card.id ? "" : card.id))}
                      style={{
                        background: T.card,
                        border: `1px solid ${open ? T.accent : T.border}`,
                        borderRadius: 18,
                        padding: 16,
                        display: "grid",
                        gap: 10,
                        cursor: "pointer",
                        boxShadow: open ? `0 0 0 2px color-mix(in srgb, ${T.accent} 28%, transparent)` : "none",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: T.muted }}>{card.title}</div>
                          <div style={{ marginTop: 8, fontWeight: 900, color: T.text, lineHeight: 1 }}>{renderMetricValue(card.value, T.text, { numberSize: 28, unitSize: 16, gap: 2 })}</div>
                        </div>
                        {card.visual?.src ? (
                          <img src={card.visual.src} alt={card.visual.label || card.title} style={{ width: 54, height: 54, objectFit: "contain", borderRadius: 12 }} />
                        ) : (
                          <div style={{ fontSize: 24 }}>{card.accent}</div>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: T.muted }}>{card.helper}</div>
                      {open && (
                        <div style={{ marginTop: 6, paddingTop: 12, borderTop: `1px solid ${T.border}`, display: "grid", gap: 8 }}>
                          <div style={{ fontSize: 14, color: T.text, lineHeight: 1.55 }}>{card.detail}</div>
                          <div style={{ fontSize: 12, color: T.muted }}>
                            Estação usada: <strong style={{ color: T.text }}>{sourceLabel || "principal da fazenda"}</strong>{observedOn ? ` · ${fmtDate(observedOn)}` : ""}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeView === "summaries" && (
          <div style={{ display: "grid", gap: 14 }}>
            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 18, padding: 16, display: "grid", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>Resumos Climáticos</div>
                  <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
                    Comportamento médio do clima em janelas de tempo, usando a estação principal consolidada da fazenda.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <Badge>{sourceLabel || "Sem estação principal"}</Badge>
                  <Btn size="sm" variant="ghost" onClick={() => loadPeriodSummaries({ autoSync: false })} disabled={loadingPeriods}>{loadingPeriods ? "Atualizando..." : "Atualizar resumos"}</Btn>
                </div>
              </div>
              <div style={{ display: "grid", gap: 8, color: T.muted, fontSize: 13 }}>
                <div><strong style={{ color: T.text }}>Base dos resumos:</strong> {periodSummary?.effective_source ? String(periodSummary.effective_source).toUpperCase() : "Sem fonte definida"}</div>
                <div><strong style={{ color: T.text }}>Último dia consolidado:</strong> {periodSummary?.latest_observed_on ? fmtDate(periodSummary.latest_observed_on) : "Sem consolidação"}</div>
                <div><strong style={{ color: T.text }}>Indicadores:</strong> temperatura média, umidade média e chuva acumulada.</div>
              </div>
            </div>

            {!hasPeriodData ? (
              <div style={{ background: T.card, border: `1px dashed ${T.border}`, borderRadius: 18, padding: 18, display: "grid", gap: 10 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>Sem dados suficientes para montar os resumos</div>
                <div style={{ fontSize: 14, color: T.muted, lineHeight: 1.5 }}>
                  Os resumos climáticos aparecem quando a estação principal já acumulou leituras consolidadas no banco. Continue sincronizando a estação ou aguarde novas leituras do cron climático.
                </div>
                <div>
                  <Btn onClick={() => loadPeriodSummaries({ autoSync: false })} disabled={loadingPeriods}>{loadingPeriods ? "Carregando..." : "Tentar novamente"}</Btn>
                </div>
              </div>
            ) : (
              <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))" }}>
                {periodCards.map((card) => {
                  const open = expandedCard === card.id;
                  const meta = periodStatusMeta(card.status);
                  return (
                    <div
                      key={card.id}
                      onClick={() => setExpandedCard((prev) => (prev === card.id ? "" : card.id))}
                      style={{
                        background: T.card,
                        border: `1px solid ${open ? T.accent : T.border}`,
                        borderRadius: 18,
                        padding: 16,
                        display: "grid",
                        gap: 10,
                        cursor: "pointer",
                        boxShadow: open ? `0 0 0 2px color-mix(in srgb, ${T.accent} 28%, transparent)` : "none",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: T.muted }}>{card.title}</div>
                          <div style={{ marginTop: 8, fontSize: 26, fontWeight: 900, color: T.text, lineHeight: 1 }}>{card.valuePrimary}</div>
                        </div>
                        <div style={{ fontSize: 24 }}>{card.accent}</div>
                      </div>
                      <div style={{ display: "grid", gap: 6, fontSize: 12, color: T.muted }}>
                        <div>Umidade média: <strong style={{ color: T.text }}>{card.valueSecondary}</strong></div>
                        <div>Chuva acumulada: <strong style={{ color: T.text }}>{card.rainfallLabel}</strong></div>
                        <div>{card.helper}</div>
                      </div>
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, width: "fit-content", borderRadius: 999, padding: "6px 9px", fontSize: 11, fontWeight: 700, background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}44` }}>
                        <span style={{ width: 8, height: 8, borderRadius: 999, background: meta.color }} />
                        {meta.label}
                      </div>
                      {open && (
                        <div style={{ marginTop: 6, paddingTop: 12, borderTop: `1px solid ${T.border}`, display: "grid", gap: 8 }}>
                          <div style={{ fontSize: 14, color: T.text, lineHeight: 1.55 }}>
                            {card.detail?.has_data
                              ? `Período ${formatPeriodRange(fmtDate, card.detail)}. Temperatura média ${card.valuePrimary}, umidade média ${card.valueSecondary} e chuva acumulada de ${card.rainfallLabel}.`
                              : "Este período ainda não possui dados consolidados suficientes para mostrar um resumo climático confiável."}
                          </div>
                          <div style={{ fontSize: 12, color: T.muted, display: "grid", gap: 4 }}>
                            <div>Número de leituras usadas: <strong style={{ color: T.text }}>{Number(card.detail?.readings_count || 0)}</strong></div>
                            <div>Janela analisada: <strong style={{ color: T.text }}>{Number(card.detail?.days_expected || card.detail?.window_days || 0)} dias</strong></div>
                            <div>Estação usada: <strong style={{ color: T.text }}>{sourceLabel || "principal da fazenda"}</strong></div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeView === "history" && (
          <div style={{ display: "grid", gap: 14 }}>
            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 18, padding: 16, display: "grid", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>Histórico Climático</div>
                  <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
                    Lista diária consolidada do clima da fazenda, usando o banco climático já gravado pela estação e pelo cron.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {historyOptions.map((days) => (
                      <Btn key={days} size="sm" variant={historyFilterMode !== "custom" && selectedHistoryDays === days ? "primary" : "ghost"} onClick={() => { setHistoryFilterMode("preset"); setHistoryFilterError(""); setHistoryPeriodDays(days); }}>{days} dias</Btn>
                    ))}
                  </div>
                  <Btn size="sm" variant={historyFilterMode === "custom" ? "primary" : "ghost"} onClick={() => { setHistoryFilterMode((prev) => prev === "custom" ? "preset" : "custom"); setHistoryFilterError(""); }}>{historyFilterMode === "custom" ? "Usando personalizado" : "Período personalizado"}</Btn>
                  <Btn size="sm" variant="ghost" onClick={() => loadHistory({ limit: 365 })} disabled={loadingHistory}>{loadingHistory ? "Atualizando..." : "Atualizar histórico"}</Btn>
                </div>
              </div>

              {historyFilterMode === "custom" && (
                <div style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 14, padding: 14, display: "grid", gap: 12 }}>
                  <div style={{ display: "grid", gap: 4 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: T.text }}>Período personalizado</div>
                    <div style={{ fontSize: 12, color: T.muted }}>Escolha data inicial e final. O intervalo máximo por consulta é de 365 dias.</div>
                  </div>
                  <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
                    <label style={{ display: "grid", gap: 6, fontSize: 12, color: T.muted }}>
                      <span>Data inicial</span>
                      <input type="date" value={historyCustomStart} max={historyCustomEnd || undefined} onChange={(e) => { setHistoryCustomStart(e.target.value); setHistoryFilterError(""); }} style={{ background: T.inputBg, color: T.text, border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 12px" }} />
                    </label>
                    <label style={{ display: "grid", gap: 6, fontSize: 12, color: T.muted }}>
                      <span>Data final</span>
                      <input type="date" value={historyCustomEnd} min={historyCustomStart || undefined} onChange={(e) => { setHistoryCustomEnd(e.target.value); setHistoryFilterError(""); }} style={{ background: T.inputBg, color: T.text, border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 12px" }} />
                    </label>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <Btn size="sm" onClick={() => {
                      const startDate = normalizeHistoryDateValue(historyCustomStart);
                      const endDate = normalizeHistoryDateValue(historyCustomEnd);
                      if (!startDate || !endDate) {
                        setHistoryFilterError("Escolha as duas datas para aplicar o período personalizado.");
                        return;
                      }
                      if (startDate > endDate) {
                        setHistoryFilterError("A data inicial não pode ser maior que a data final.");
                        return;
                      }
                      const totalDays = countDaysBetween(startDate, endDate);
                      if (!totalDays || totalDays > 365) {
                        setHistoryFilterError("O período personalizado aceita no máximo 365 dias.");
                        return;
                      }
                      setHistoryFilterMode("custom");
                      setHistoryFilterError("");
                    }}>Aplicar período</Btn>
                    <Btn size="sm" variant="ghost" onClick={() => {
                      setHistoryFilterMode("preset");
                      setHistoryCustomStart("");
                      setHistoryCustomEnd("");
                      setHistoryFilterError("");
                    }}>Limpar personalizado</Btn>
                    <div style={{ fontSize: 12, color: T.muted }}>
                      {historyCustomDayCount ? `${historyCustomDayCount} dia${historyCustomDayCount > 1 ? "s" : ""} no intervalo selecionado.` : "Selecione um intervalo para filtrar o histórico."}
                    </div>
                  </div>
                  {!!historyFilterError && <div style={{ fontSize: 12, color: T.danger }}>{historyFilterError}</div>}
                </div>
              )}

              <div style={{ fontSize: 12, color: T.muted }}>{historyAppliedLabel} · {historyStats.days} dia{historyStats.days === 1 ? "" : "s"} exibido{historyStats.days === 1 ? "" : "s"}.</div>

              <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
                <div style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 14, padding: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.muted }}>Dias exibidos</div>
                  <div style={{ marginTop: 8, fontSize: 22, fontWeight: 900, color: T.text }}>{historyStats.days}</div>
                </div>
                <div style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 14, padding: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.muted }}>Chuva acumulada</div>
                  <div style={{ marginTop: 8, fontSize: 22, fontWeight: 900, color: T.text }}>{historyStats.rainfallTotal}</div>
                </div>
                <div style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 14, padding: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.muted }}>Temperatura média</div>
                  <div style={{ marginTop: 8, fontSize: 22, fontWeight: 900, color: T.text }}>{historyStats.avgTemp}</div>
                </div>
                <div style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 14, padding: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.muted }}>Umidade média</div>
                  <div style={{ marginTop: 8, fontSize: 22, fontWeight: 900, color: T.text }}>{historyStats.avgHumidity}</div>
                </div>
              </div>
            </div>

            {!visibleHistoryRows.length ? (
              <div style={{ background: T.card, border: `1px dashed ${T.border}`, borderRadius: 18, padding: 18, display: "grid", gap: 10 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>Sem histórico diário ainda</div>
                <div style={{ fontSize: 14, color: T.muted, lineHeight: 1.5 }}>
                  O Trembo ainda não encontrou linhas suficientes em <strong style={{ color: T.text }}>fz_farm_climate_daily</strong> para mostrar o histórico consolidado da fazenda.
                </div>
                <div>
                  <Btn onClick={() => loadHistory({ limit: 365 })} disabled={loadingHistory}>{loadingHistory ? "Carregando..." : "Tentar novamente"}</Btn>
                </div>
              </div>
            ) : (
              <div style={{ display: "grid", gap: 12 }}>
                {visibleHistoryRows.map((row) => {
                  const open = expandedCard === `history_${row.id}`;
                  return (
                    <div key={row.id} onClick={() => setExpandedCard((prev) => (prev === `history_${row.id}` ? "" : `history_${row.id}`))} style={{ background: T.card, border: `1px solid ${open ? T.accent : T.border}`, borderRadius: 18, padding: 16, display: "grid", gap: 12, cursor: "pointer", boxShadow: open ? `0 0 0 2px ${T.accent}22` : "none" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                        <div style={{ display: "grid", gap: 6 }}>
                          <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>{row.observedOn ? fmtDate(row.observedOn) : "Dia sem data"}</div>
                          <div style={{ fontSize: 13, color: T.muted }}>{row.sourceLabel}</div>
                        </div>
                        {row.visual?.src ? (
                          <img src={row.visual.src} alt={row.visual.label || 'Histórico climático'} style={{ width: 54, height: 54, objectFit: 'contain' }} />
                        ) : null}
                      </div>

                      <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}>
                        <div style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 12, padding: 12 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: T.muted }}>Chuva</div>
                          <div style={{ marginTop: 6, fontSize: 18, fontWeight: 900, color: T.text }}>{row.rainLabel}</div>
                        </div>
                        <div style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 12, padding: 12 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: T.muted }}>Temperatura média</div>
                          <div style={{ marginTop: 6, fontSize: 18, fontWeight: 900, color: T.text }}>{row.tempLabel}</div>
                        </div>
                        <div style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 12, padding: 12 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: T.muted }}>Umidade média</div>
                          <div style={{ marginTop: 6, fontSize: 18, fontWeight: 900, color: T.text }}>{row.humidityLabel}</div>
                        </div>
                        <div style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 12, padding: 12 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: T.muted }}>Pressão</div>
                          <div style={{ marginTop: 6, fontSize: 18, fontWeight: 900, color: T.text }}>{row.pressureLabel}</div>
                        </div>
                      </div>

                      {open && (
                        <div style={{ marginTop: 2, paddingTop: 12, borderTop: `1px solid ${T.border}`, display: "grid", gap: 8 }}>
                          <div style={{ fontSize: 13, color: T.muted, display: "grid", gap: 4 }}>
                            <div><strong style={{ color: T.text }}>Mín / Máx:</strong> {row.tempRangeLabel}</div>
                            <div><strong style={{ color: T.text }}>Umidade mín / máx:</strong> {row.humidityRangeLabel}</div>
                            <div><strong style={{ color: T.text }}>Vento médio:</strong> {row.windLabel}</div>
                            <div><strong style={{ color: T.text }}>Persistido em:</strong> {row.createdAt ? fmtDate(row.createdAt) : 'Sem data de gravação'}</div>
                          </div>
                          {!!row.notes && <div style={{ fontSize: 13, color: T.text, lineHeight: 1.5 }}><strong>Anotação:</strong> {row.notes}</div>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeView === "comparisons" && (
          <div style={{ display: "grid", gap: 14 }}>
            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 18, padding: 16, display: "grid", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>Comparativos entre períodos</div>
                  <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
                    Leitura comparativa da janela atual contra o período anterior equivalente, usando o histórico climático consolidado da fazenda.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  {[7, 30, 90, 180].filter((days) => historyRows.length >= days * 2).map((days) => (
                    <Btn key={days} size="sm" variant={comparisonDays === days ? "primary" : "ghost"} onClick={() => setComparisonDays(days)}>{days} dias</Btn>
                  ))}
                  <Btn size="sm" variant="ghost" onClick={() => loadHistory({ limit: 365 })} disabled={loadingHistory}>{loadingHistory ? "Atualizando..." : "Atualizar base"}</Btn>
                </div>
              </div>
              <div style={{ display: "grid", gap: 8, color: T.muted, fontSize: 13 }}>
                <div><strong style={{ color: T.text }}>Janela atual:</strong> {comparisonSummary?.currentStats?.startDate && comparisonSummary?.currentStats?.endDate ? `${fmtDate(comparisonSummary.currentStats.startDate)} até ${fmtDate(comparisonSummary.currentStats.endDate)}` : "Sem janela atual suficiente"}</div>
                <div><strong style={{ color: T.text }}>Janela anterior:</strong> {comparisonSummary?.previousStats?.startDate && comparisonSummary?.previousStats?.endDate ? `${fmtDate(comparisonSummary.previousStats.startDate)} até ${fmtDate(comparisonSummary.previousStats.endDate)}` : "Sem janela anterior equivalente"}</div>
                <div><strong style={{ color: T.text }}>Base:</strong> comparação em blocos equivalentes de {comparisonSummary?.windowDays || comparisonDays} dias, sem gráficos.</div>
              </div>
            </div>

            {!comparisonSummary?.enoughData ? (
              <div style={{ background: T.card, border: `1px dashed ${T.border}`, borderRadius: 18, padding: 18, display: "grid", gap: 10 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>Sem base suficiente para comparar períodos</div>
                <div style={{ fontSize: 14, color: T.muted, lineHeight: 1.5 }}>
                  Para comparar duas janelas equivalentes, o Trembo precisa de pelo menos dois blocos completos de histórico climático. Continue acumulando leituras diárias consolidadas no banco da fazenda.
                </div>
                <div>
                  <Btn onClick={() => loadHistory({ limit: 365 })} disabled={loadingHistory}>{loadingHistory ? "Carregando..." : "Atualizar histórico"}</Btn>
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))" }}>
                  {comparisonSummary.cards.map((card) => {
                    const open = expandedCard === `comparison_${card.id}`;
                    const meta = comparisonToneMeta(card.delta?.tone);
                    return (
                      <div key={card.id} onClick={() => setExpandedCard((prev) => (prev === `comparison_${card.id}` ? "" : `comparison_${card.id}`))} style={{ background: T.card, border: `1px solid ${open ? meta.color : T.border}`, borderRadius: 18, padding: 16, display: "grid", gap: 10, cursor: "pointer", boxShadow: open ? `0 0 0 2px ${meta.color}22` : "none" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: T.muted }}>{card.title}</div>
                            <div style={{ marginTop: 8, fontSize: 26, fontWeight: 900, color: T.text, lineHeight: 1 }}>{card.value}</div>
                          </div>
                          <div style={{ fontSize: 24 }}>{card.accent}</div>
                        </div>
                        <div style={{ display: "grid", gap: 6, fontSize: 12, color: T.muted }}>
                          <div>Período anterior: <strong style={{ color: T.text }}>{card.previousValue}</strong></div>
                          <div>{card.delta?.label || "Comparação indisponível"}</div>
                        </div>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, width: "fit-content", borderRadius: 999, padding: "6px 9px", fontSize: 11, fontWeight: 700, background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}44` }}><span style={{ width: 8, height: 8, borderRadius: 999, background: meta.color }} />{meta.label}</div>
                        {open && (
                          <div style={{ marginTop: 6, paddingTop: 12, borderTop: `1px solid ${T.border}`, display: "grid", gap: 8 }}>
                            <div style={{ fontSize: 14, color: T.text, lineHeight: 1.55 }}>{card.narrative}</div>
                            <div style={{ fontSize: 12, color: T.muted, display: "grid", gap: 4 }}>
                              <div>Janela atual: <strong style={{ color: T.text }}>{comparisonSummary.currentStats.readings} dia(s)</strong></div>
                              <div>Janela anterior: <strong style={{ color: T.text }}>{comparisonSummary.previousStats.readings} dia(s)</strong></div>
                              <div>Fonte operacional: <strong style={{ color: T.text }}>{sourceLabel || "Estação principal da fazenda"}</strong></div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 18, padding: 16, display: "grid", gap: 12 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>Leitura rápida do comparativo</div>
                  <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                    <div style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 14, padding: 14, display: "grid", gap: 6 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: T.muted }}>Temperatura x chuva</div>
                      <div style={{ fontSize: 13, color: T.text, lineHeight: 1.5 }}>
                        {comparisonSummary.currentStats.avgTemp != null && comparisonSummary.previousStats.avgTemp != null && comparisonSummary.currentStats.rainfall != null && comparisonSummary.previousStats.rainfall != null
                          ? (comparisonSummary.currentStats.avgTemp > comparisonSummary.previousStats.avgTemp && comparisonSummary.currentStats.rainfall < comparisonSummary.previousStats.rainfall
                              ? 'A janela atual está mais quente e mais seca do que a anterior equivalente.'
                              : (comparisonSummary.currentStats.avgTemp < comparisonSummary.previousStats.avgTemp && comparisonSummary.currentStats.rainfall > comparisonSummary.previousStats.rainfall
                                ? 'A janela atual está mais amena e mais úmida do que a anterior equivalente.'
                                : 'Temperatura e chuva mudaram, mas sem um padrão simples dominante entre as duas janelas.'))
                          : 'Ainda não há base suficiente para combinar temperatura e chuva na leitura rápida.'}
                      </div>
                    </div>
                    <div style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 14, padding: 14, display: "grid", gap: 6 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: T.muted }}>Síntese operacional</div>
                      <div style={{ fontSize: 13, color: T.text, lineHeight: 1.5 }}>
                        {comparisonSummary.currentStats.rainfall != null && comparisonSummary.previousStats.rainfall != null
                          ? (comparisonSummary.currentStats.rainfall > comparisonSummary.previousStats.rainfall
                              ? 'O período atual mostra recuperação hídrica melhor do que o período anterior equivalente.'
                              : (comparisonSummary.currentStats.rainfall < comparisonSummary.previousStats.rainfall
                                ? 'O período atual pede mais atenção hídrica do que o período anterior equivalente.'
                                : 'A chuva acumulada ficou estável entre os dois períodos equivalentes.'))
                          : 'A síntese operacional ficará mais robusta conforme o histórico consolidado crescer.'}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {activeView === "alerts" && (
          <div style={{ display: "grid", gap: 14 }}>
            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 18, padding: 16, display: "grid", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>Alertas Climáticos</div>
                  <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
                    Leitura base de risco climático com foco em calor, umidade, chuva, seca, atraso de leitura e saúde das estações.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <StatusChip color={overallAlertMeta.color} label={alertsSummary?.overall_label || overallAlertMeta.label} />
                  <Btn size="sm" variant="ghost" onClick={() => loadAlerts({ autoSync: false })} disabled={loadingAlerts}>{loadingAlerts ? "Atualizando..." : "Atualizar alertas"}</Btn>
                </div>
              </div>
              <div style={{ display: "grid", gap: 8, color: T.muted, fontSize: 13 }}>
                <div><strong style={{ color: T.text }}>Status geral:</strong> {String(alertsSummary?.status_headline || "Sem status consolidado")}</div>
                <div><strong style={{ color: T.text }}>Resumo:</strong> {String(alertsSummary?.status_summary || "Sem alertas climáticos calculados ainda.")}</div>
                <div><strong style={{ color: T.text }}>Estação usada:</strong> {alertsSummary?.source_station_name || sourceLabel || "Sem estação principal"}</div>
              </div>
            </div>

            {!activeAlertCards.length && !recentAlertCards.length ? (
              <div style={{ background: T.card, border: `1px dashed ${T.border}`, borderRadius: 18, padding: 18, display: "grid", gap: 10 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>Sem alertas climáticos relevantes agora</div>
                <div style={{ fontSize: 14, color: T.muted, lineHeight: 1.5 }}>
                  O Trembo ainda não encontrou condição crítica ou de atenção suficiente na leitura atual da fazenda. Conforme novas leituras entrarem, esta área passa a destacar alertas ativos e alertas recentes.
                </div>
                <div>
                  <Btn onClick={() => loadAlerts()} disabled={loadingAlerts}>{loadingAlerts ? "Carregando..." : "Verificar novamente"}</Btn>
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: "grid", gap: 12 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>Alertas ativos</div>
                  {activeAlertCards.length ? (
                    <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
                      {activeAlertCards.map((card) => {
                        const open = expandedCard === `active_${card.id}`;
                        const meta = alertMeta(card.level);
                        return (
                          <div key={`active_${card.id}`} onClick={() => setExpandedCard((prev) => (prev === `active_${card.id}` ? "" : `active_${card.id}`))} style={{ background: T.card, border: `1px solid ${open ? meta.color : T.border}`, borderRadius: 18, padding: 16, display: "grid", gap: 10, cursor: "pointer", boxShadow: open ? `0 0 0 2px ${meta.color}22` : "none" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: T.muted }}>{card.title}</div>
                                <div style={{ marginTop: 8, fontSize: 24, fontWeight: 900, color: T.text, lineHeight: 1 }}>{card.badge || meta.label}</div>
                              </div>
                              {card.visual?.src ? (
                            <img src={card.visual.src} alt={card.visual.label || card.condition} style={{ width: 56, height: 56, objectFit: "contain" }} />
                          ) : (
                            <div style={{ fontSize: 24 }}>{card.icon}</div>
                          )}
                            </div>
                            <div style={{ fontSize: 12, color: T.muted }}>{card.summary || "Alerta climático ativo"}</div>
                            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, width: "fit-content", borderRadius: 999, padding: "6px 9px", fontSize: 11, fontWeight: 700, background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}44` }}><span style={{ width: 8, height: 8, borderRadius: 999, background: meta.color }} />{meta.label}</div>
                            {open && (
                              <div style={{ marginTop: 6, paddingTop: 12, borderTop: `1px solid ${T.border}`, display: "grid", gap: 8 }}>
                                <div style={{ fontSize: 14, color: T.text, lineHeight: 1.55 }}>{card.detail}</div>
                                <div style={{ fontSize: 12, color: T.muted }}>{card.sourceLabel ? `Estação usada: ${card.sourceLabel}` : (sourceLabel ? `Estação usada: ${sourceLabel}` : "Estação principal da fazenda")}{card.observedOn ? ` · ${fmtDate(card.observedOn)}` : ""}</div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ background: T.card, border: `1px dashed ${T.border}`, borderRadius: 18, padding: 16, color: T.muted, fontSize: 14 }}>Nenhum alerta ativo neste momento.</div>
                  )}
                </div>

                <div style={{ display: "grid", gap: 12 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>Alertas recentes</div>
                  {recentAlertCards.length ? (
                    <div style={{ display: "grid", gap: 12 }}>
                      {recentAlertCards.map((card) => {
                        const open = expandedCard === `recent_${card.id}`;
                        const meta = alertMeta(card.level);
                        return (
                          <div key={`recent_${card.id}`} onClick={() => setExpandedCard((prev) => (prev === `recent_${card.id}` ? "" : `recent_${card.id}`))} style={{ background: T.card, border: `1px solid ${open ? meta.color : T.border}`, borderRadius: 18, padding: 16, display: "grid", gap: 8, cursor: "pointer" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                              <div style={{ fontWeight: 800, color: T.text }}>{card.title}</div>
                              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, width: "fit-content", borderRadius: 999, padding: "6px 9px", fontSize: 11, fontWeight: 700, background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}44` }}><span style={{ width: 8, height: 8, borderRadius: 999, background: meta.color }} />{meta.label}</div>
                            </div>
                            <div style={{ fontSize: 13, color: T.muted }}>{card.summary}</div>
                            {open && <div style={{ fontSize: 14, color: T.text, lineHeight: 1.55, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>{card.detail}</div>}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ background: T.card, border: `1px dashed ${T.border}`, borderRadius: 18, padding: 16, color: T.muted, fontSize: 14 }}>Nenhum alerta recente registrado ainda.</div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {activeView === "pasture_impact" && (
          <div style={{ display: "grid", gap: 14 }}>
            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 18, padding: 16, display: "grid", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>Impacto em Pastagens</div>
                  <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
                    Leitura operacional do clima sobre a capacidade das pastagens da fazenda, usando a janela climática configurada e a estação principal do banco.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <StatusChip color={impactMeta.color} label={impactMeta.label} />
                  <Badge>{monitoredPastures.length ? `${monitoredPastures.length} pastagem${monitoredPastures.length > 1 ? "ens" : ""}` : "Sem pastagens"}</Badge>
                  <Btn size="sm" variant="ghost" onClick={() => loadSummary({ autoSync: true })} disabled={loading}>{loading ? "Atualizando..." : "Atualizar leitura"}</Btn>
                </div>
              </div>

              <div style={{ display: "grid", gap: 8, color: T.muted, fontSize: 13 }}>
                <div><strong style={{ color: T.text }}>Status:</strong> {impactMeta.label}</div>
                <div><strong style={{ color: T.text }}>Tendência:</strong> {trendMeta.label}</div>
                <div><strong style={{ color: T.text }}>Resumo:</strong> {impactNarrative}</div>
                <div><strong style={{ color: T.text }}>Orientação curta:</strong> {impactGuidance}</div>
              </div>
            </div>

            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
              <div style={{ background: T.card, border: `1px solid ${impactMeta.color}55`, borderRadius: 18, padding: 16, display: "grid", gap: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.muted }}>Status operacional</div>
                    <div style={{ marginTop: 8, fontSize: 28, fontWeight: 900, color: impactMeta.color, lineHeight: 1 }}>{impactMeta.label}</div>
                  </div>
                  <div style={{ fontSize: 28 }}>{impactMeta.icon}</div>
                </div>
                <div style={{ fontSize: 14, color: T.text, lineHeight: 1.55 }}>{impactNarrative}</div>
              </div>

              <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 18, padding: 16, display: "grid", gap: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.muted }}>Tendência</div>
                <div style={{ marginTop: 8, fontSize: 24, fontWeight: 900, color: T.text, lineHeight: 1 }}>{trendMeta.label}</div>
                <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.5 }}>{trendMeta.detail}</div>
                <div style={{ fontSize: 12, color: T.muted, display: "grid", gap: 4 }}>
                  <div>15d: <strong style={{ color: T.text }}>{trendMeta.shortFactor.toFixed(2)}x</strong></div>
                  <div>30d/base: <strong style={{ color: T.text }}>{trendMeta.baseFactor.toFixed(2)}x</strong></div>
                  <div>90d: <strong style={{ color: T.text }}>{trendMeta.longFactor.toFixed(2)}x</strong></div>
                </div>
              </div>

              <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 18, padding: 16, display: "grid", gap: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.muted }}>Capacidade climática ajustada</div>
                <div style={{ marginTop: 8, fontSize: 24, fontWeight: 900, color: T.text, lineHeight: 1 }}>{pastureAdjustedUaTotal.toFixed(2)} UA</div>
                <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.5 }}>Base monitorada: {pastureBaseUaTotal.toFixed(2)} UA · fator {climateCalc?.factorLabel || "1.00x"}</div>
              </div>
            </div>

            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 18, padding: 16, display: "grid", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>Fatores considerados</div>
                  <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
                    Leitura resumida dos fatores que puxam o ajuste do clima sobre a capacidade das pastagens.
                  </div>
                </div>
                <div style={{ fontSize: 12, color: T.muted }}>Inline · pronto para a integração com Localização no próximo ZIP</div>
              </div>
              <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                {impactFactors.map((item) => (
                  <div key={item.id} style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 14, padding: 14, display: "grid", gap: 6 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.muted }}>{item.label}</div>
                    <div style={{ fontSize: 20, fontWeight: 900, color: T.text, lineHeight: 1.1 }}>{item.value}</div>
                    <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.45 }}>{item.helper}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeView === "planning" && (
          <div style={{ display: "grid", gap: 14 }}>
            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 18, padding: 16, display: "grid", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>Planejamento Operacional</div>
                  <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
                    Leitura prática para decidir melhor as próximas janelas de operação usando previsão, alertas, tendência recente e impacto em pastagens.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <StatusChip color={planningLevelMeta(planningSummary?.overallLevel).color} label={planningLevelMeta(planningSummary?.overallLevel).label} />
                  <Btn size="sm" variant="ghost" onClick={() => { loadAlerts({ autoSync: false }); loadForecast({ autoSync: false }); loadHistory({ limit: 365 }); loadSummary({ silent: true, autoSync: false }); }} disabled={loadingAlerts || loadingForecast || loadingHistory}>{(loadingAlerts || loadingForecast || loadingHistory) ? "Atualizando..." : "Atualizar leitura"}</Btn>
                </div>
              </div>
              <div style={{ display: "grid", gap: 8, color: T.muted, fontSize: 13 }}>
                <div><strong style={{ color: T.text }}>Fonte usada:</strong> {sourceLabel || "Estação principal da fazenda"}</div>
                <div><strong style={{ color: T.text }}>Resumo operacional:</strong> {planningSummary?.overallNarrative || "Sem leitura operacional montada ainda."}</div>
                <div><strong style={{ color: T.text }}>Base combinada:</strong> previsão, alertas, histórico recente e condição das pastagens.</div>
              </div>
            </div>

            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
              {(planningSummary?.windows || []).map((item) => {
                const meta = planningLevelMeta(item.level);
                return (
                  <div key={item.id} style={{ background: T.card, border: `1px solid ${meta.color}55`, borderRadius: 18, padding: 16, display: "grid", gap: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.muted }}>{item.title}</div>
                        <div style={{ marginTop: 6, fontSize: 24, fontWeight: 900, color: T.text }}>{meta.label}</div>
                      </div>
                      <div style={{ fontSize: 24 }}>{meta.icon}</div>
                    </div>
                    <div style={{ fontSize: 12, color: T.muted }}>{item.subtitle}</div>
                    <div style={{ fontSize: 13, color: T.text, lineHeight: 1.55 }}>{item.narrative}</div>
                    <div style={{ fontSize: 12, color: T.muted }}>{item.helper}</div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
              {(planningSummary?.signals || []).map((item) => {
                const meta = planningLevelMeta(item.level);
                return (
                  <div key={item.id} style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 16, padding: 14, display: "grid", gap: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.muted }}>{item.title}</div>
                      <div style={{ fontSize: 20 }}>{item.icon}</div>
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: T.text }}>{item.value}</div>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, width: "fit-content", borderRadius: 999, padding: "6px 9px", fontSize: 11, fontWeight: 700, background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}44` }}><span style={{ width: 8, height: 8, borderRadius: 999, background: meta.color }} />{meta.label}</div>
                    <div style={{ fontSize: 13, color: T.text, lineHeight: 1.5 }}>{item.narrative}</div>
                  </div>
                );
              })}
            </div>

            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 18, padding: 16, display: "grid", gap: 12 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>Orientações rápidas</div>
              <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                {(planningSummary?.recommendationList || []).map((item, idx) => (
                  <div key={`rec_${idx}`} style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 14, padding: 14, fontSize: 13, color: T.text, lineHeight: 1.55 }}>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}




        {activeView === "forecast" && (
          <div style={{ display: "grid", gap: 14 }}>
            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 18, padding: 16, display: "grid", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>Previsão do Tempo</div>
                  <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
                    Leitura futura base do clima para hoje e os próximos dias, usando a estação principal e a janela recente do banco climático.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <StatusChip color={forecastMeta.color} label={forecastSummary?.status?.label || forecastMeta.label} />
                  <Btn size="sm" variant="ghost" onClick={() => loadForecast({ autoSync: false })} disabled={loadingForecast}>{loadingForecast ? "Atualizando..." : "Atualizar previsão"}</Btn>
                </div>
              </div>
              <div style={{ display: "grid", gap: 8, color: T.muted, fontSize: 13 }}>
                <div><strong style={{ color: T.text }}>Resumo de hoje:</strong> {String(forecastSummary?.today?.headline || forecastSummary?.status?.summary || "Sem previsão consolidada ainda.")}</div>
                <div><strong style={{ color: T.text }}>Estação usada:</strong> {forecastSummary?.source_station_name || sourceLabel || "Sem estação principal"}</div>
                <div><strong style={{ color: T.text }}>Janela considerada:</strong> {String(forecastSummary?.window_label || "Hoje, amanhã, próximos 3 dias e próximos 7 dias")}</div>
              </div>
            </div>

            {!forecastCards.length ? (
              <div style={{ background: T.card, border: `1px dashed ${T.border}`, borderRadius: 18, padding: 18, display: "grid", gap: 10 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>Sem previsão disponível ainda</div>
                <div style={{ fontSize: 14, color: T.muted, lineHeight: 1.5 }}>
                  O Trembo ainda não conseguiu montar uma previsão base com dados suficientes da fazenda. Assim que a base climática crescer e a estação principal continuar alimentando o banco, esta área passa a mostrar hoje, amanhã e os próximos dias.
                </div>
                <div>
                  <Btn onClick={() => loadForecast()} disabled={loadingForecast}>{loadingForecast ? "Carregando..." : "Tentar novamente"}</Btn>
                </div>
              </div>
            ) : (
              <>
                <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 18, padding: 16, display: "grid", gap: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>Resumo de hoje</div>
                    <div style={{ fontSize: 12, color: T.muted }}>{forecastSummary?.today?.date ? fmtDate(forecastSummary.today.date) : "Hoje"}</div>
                  </div>
                  <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
                    <div style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 14, padding: 14, display:"grid", gap:10, justifyItems:"start" }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.muted }}>Condição</div>
                      {buildForecastVisualMeta(forecastSummary?.today || {})?.src ? (
                        <img src={buildForecastVisualMeta(forecastSummary?.today || {}).src} alt={buildForecastVisualMeta(forecastSummary?.today || {}).label || (forecastSummary?.today?.condition_label || "Previsão")} style={{ width: 74, height: 74, objectFit: "contain" }} />
                      ) : null}
                      <div style={{ marginTop: 2, fontSize: 24, fontWeight: 900, color: T.text }}>{forecastSummary?.today?.condition_label || "Sem condição"}</div>
                    </div>
                    <div style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 14, padding: 14 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.muted }}>Temperaturas</div>
                      <div style={{ marginTop: 8, fontSize: 20, fontWeight: 900, color: T.text }}>{forecastSummary?.today?.temp_min_c == null ? "—" : `${Number(forecastSummary.today.temp_min_c).toFixed(1)} °C`} · {forecastSummary?.today?.temp_max_c == null ? "—" : `${Number(forecastSummary.today.temp_max_c).toFixed(1)} °C`}</div>
                    </div>
                    <div style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 14, padding: 14 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.muted }}>Chuva prevista</div>
                      <div style={{ marginTop: 8, fontSize: 20, fontWeight: 900, color: T.text }}>{forecastSummary?.today?.rain_chance_pct == null ? "—" : `${Number(forecastSummary.today.rain_chance_pct).toFixed(0)}%`} · {forecastSummary?.today?.rainfall_mm == null ? "—" : `${Number(forecastSummary.today.rainfall_mm).toFixed(1)} mm`}</div>
                    </div>
                  </div>
                </div>

                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                  {forecastCards.map((card) => {
                    const open = expandedCard === `forecast_${card.id}`;
                    return (
                      <div key={card.id} onClick={() => setExpandedCard((prev) => (prev === `forecast_${card.id}` ? "" : `forecast_${card.id}`))} style={{ background: T.card, border: `1px solid ${open ? T.accent : T.border}`, borderRadius: 18, padding: 16, display: "grid", gap: 10, cursor: "pointer", boxShadow: open ? `0 0 0 2px ${T.accent}22` : "none" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: T.muted }}>{card.title}</div>
                            <div style={{ marginTop: 8, fontSize: 20, fontWeight: 900, color: T.text, lineHeight: 1 }}>{card.condition}</div>
                          </div>
                          {card.visual?.src ? (
                            <img src={card.visual.src} alt={card.visual.label || card.condition} style={{ width: 56, height: 56, objectFit: "contain" }} />
                          ) : (
                            <div style={{ fontSize: 24 }}>{card.icon}</div>
                          )}
                        </div>
                        <div style={{ display: "grid", gap: 4, color: T.muted, fontSize: 13 }}>
                          <div><strong style={{ color: T.text }}>Mín / Máx:</strong> {card.minLabel} · {card.maxLabel}</div>
                          <div><strong style={{ color: T.text }}>Chance de chuva:</strong> {card.rainChanceLabel}</div>
                          <div><strong style={{ color: T.text }}>Chuva prevista:</strong> {card.rainfallLabel}</div>
                        </div>
                        {open && (
                          <div style={{ marginTop: 6, paddingTop: 12, borderTop: `1px solid ${T.border}`, display: "grid", gap: 8 }}>
                            <div style={{ fontSize: 14, color: T.text, lineHeight: 1.55 }}>
                              {String(card.detail?.detail || card.detail?.summary || "Leitura base da previsão gerada a partir da estação principal e do histórico recente do clima da fazenda.")}
                            </div>
                            <div style={{ fontSize: 12, color: T.muted, display: "grid", gap: 4 }}>
                              <div>Data: <strong style={{ color: T.text }}>{card.detail?.date ? fmtDate(card.detail.date) : card.title}</strong></div>
                              <div>Fonte: <strong style={{ color: T.text }}>{forecastSummary?.source_station_name || sourceLabel || "Estação principal"}</strong></div>
                              <div>Confiabilidade: <strong style={{ color: T.text }}>{String(card.detail?.confidence_label || forecastSummary?.status?.label || "Base")}</strong></div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {!!toast && <Toast toast={toast} />}
      </div>
    );
  }

  return ClimateModule;
}
